/* eslint-disable react/function-component-definition, react/prop-types */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { TextField, useTheme, useMediaQuery, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import Swal from "sweetalert2";
import api from "api/api";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";

import useAccountMembersheetData, { parseNumber, formatNumber } from "./accountMemberSheetData";
import LoadingScreen from "layouts/loading/loadingscreen";
import { API_BASE_URL } from "config";
import { buildFileDownloadUrl } from "utils/fileDownloadUrl";
import { canEditSensitiveField, maskSensitiveFieldValue } from "utils/maskingUtils";
import PreviewOverlay from "utils/PreviewOverlay";
import {
  HEADOFFICE_DOCUMENT_FILE_ACCEPT,
  getHeadOfficeDocumentPreviewKind,
  isHeadOfficeDocumentSupportedFile,
} from "utils/headOfficeDocumentImageUtils";

const normalizeMemberFilePath = (value) => {
  const path = String(value ?? "").trim();
  if (!path) return "";
  if (/^(https?:\/\/|blob:|data:)/i.test(path)) return path;
  if (path.startsWith("/image/")) return path;
  if (path.startsWith("image/")) return `/${path}`;
  return path.startsWith("/") ? `/image${path}` : `/image/${path}`;
};

// 인사 -> 현장관리 -> 현장 직원관리
// ✅ Autocomplete 검색어를 라벨 기준으로만 가볍게 필터링
const filterOptionsByLabel = (options, state) => {
  const q = String(state?.inputValue ?? "")
    .trim()
    .toLowerCase();
  if (!q) return options;
  return options.filter((option) => String(option?.label ?? "").toLowerCase().includes(q));
};

// ✅ 상단 검색/버튼 영역을 분리해서 입력 중 본문 테이블 전체 리렌더링을 줄임
const AccountMemberToolbar = React.memo(function AccountMemberToolbar({
  isMobile,
  isCompactLayout,
  activeStatus,
  onActiveStatusChange,
  accountOptions,
  selectedAccountId,
  onSelectAccount,
  onSearchMember,
  onOpenWorkSystemModal,
  onOpenUtilModal,
  onOpenDispatchModal,
  onDownloadExcel,
  excelDownloading,
  maskingEnabled,
  onToggleMasking,
  onAddRow,
  onSave,
}) {
  const [memberInput, setMemberInput] = useState("");
  const [accountInput, setAccountInput] = useState("");
  const suppressAccountResetRef = useRef(false);

  const selectedAccountOption = useMemo(() => {
    const value = String(selectedAccountId ?? "");
    return accountOptions.find((option) => option.value === value) || null;
  }, [accountOptions, selectedAccountId]);

  useEffect(() => {
    if (suppressAccountResetRef.current) return;
    setAccountInput(selectedAccountOption?.label ?? "");
  }, [selectedAccountOption?.label, selectedAccountOption?.value]);

  const selectAccountByInput = useCallback(() => {
    suppressAccountResetRef.current = false;
    setMemberInput("");
    onSearchMember("");

    const q = String(accountInput || "").trim();
    if (!q) return;

    const qLower = q.toLowerCase();
    const exact = accountOptions.find((option) => String(option?.label ?? "").toLowerCase() === qLower);
    const partial =
      exact ||
      accountOptions.find((option) =>
        String(option?.label ?? "")
          .toLowerCase()
          .includes(qLower)
      );

    if (partial) {
      setAccountInput(partial.label || q);
      onSelectAccount(partial.value);
    }
  }, [accountInput, accountOptions, onSearchMember, onSelectAccount]);

  const selectMemberByInput = useCallback(() => {
    const q = String(memberInput || "").trim();
    suppressAccountResetRef.current = true;
    setAccountInput("");
    setMemberInput(q);
    onSearchMember(q);
  }, [memberInput, onSearchMember]);

  return (
    <MDBox
      pt={1}
      pb={1}
      sx={{
        display: "flex",
        justifyContent: isCompactLayout ? "flex-start" : "flex-end",
        alignItems: "center",
        gap: isCompactLayout ? 1 : 2,
        flexWrap: isCompactLayout ? "wrap" : "nowrap",
        position: "sticky",
        zIndex: 10,
        top: 78,
        backgroundColor: "#ffffff",
      }}
    >
      <TextField
        select
        size="small"
        value={activeStatus}
        onChange={(e) => onActiveStatusChange(e.target.value)}
        sx={{ minWidth: 150 }}
        SelectProps={{ native: true }}
      >
        <option value="N">재직자</option>
        <option value="Y">퇴사자</option>
      </TextField>

      <TextField
        size="small"
        value={memberInput}
        onChange={(e) => setMemberInput(e.target.value)}
        label="직원 검색"
        placeholder="직원명을 입력"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            selectMemberByInput();
          }
        }}
        sx={{
          minWidth: 200,
          ...(isCompactLayout ? { flex: "1 1 220px" } : {}),
          "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                  "& .MuiInputLabel-root": { fontSize: 12 },
          "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
        }}
      />

      <Autocomplete
        size="small"
        sx={{
          minWidth: 200,
          ...(isCompactLayout ? { flex: "1 1 220px" } : {}),
        }}
        options={accountOptions}
        value={selectedAccountOption}
        onChange={(_, option) => {
          if (!option) return;
          suppressAccountResetRef.current = false;
          setMemberInput("");
          setAccountInput(option.label ?? "");
          onSearchMember("");
          onSelectAccount(option.value);
        }}
        inputValue={accountInput}
        onInputChange={(_, newValue, reason) => {
          if (reason === "reset" && suppressAccountResetRef.current) return;
          if (reason === "input" || reason === "clear") {
            suppressAccountResetRef.current = false;
          }
          setAccountInput(newValue);
        }}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={filterOptionsByLabel}
        renderInput={(params) => (
          <TextField
            {...params}
            label="거래처 검색"
            placeholder="거래처명을 입력"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                selectAccountByInput();
              }
            }}
            sx={{
              "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                  "& .MuiInputLabel-root": { fontSize: 12 },
              "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
            }}
          />
        )}
      />

      <MDButton variant="gradient" color="warning" onClick={onOpenWorkSystemModal}>
        근무형태 관리
      </MDButton>

      <MDButton variant="gradient" color="warning" onClick={onOpenUtilModal}>
        통합/유틸 관리
      </MDButton>

      <MDButton variant="gradient" color="warning" onClick={onOpenDispatchModal}>
        직원파출관리
      </MDButton>

      <MDButton variant="gradient" color="dark" onClick={onDownloadExcel} disabled={excelDownloading}>
        {excelDownloading ? "다운로드 중..." : "전체 거래처 엑셀"}
      </MDButton>

      <MDButton variant="outlined" color={maskingEnabled ? "dark" : "secondary"} onClick={onToggleMasking}>
        {maskingEnabled ? "* 해제" : "* 적용"}
      </MDButton>

      <MDButton variant="gradient" color="success" onClick={onAddRow}>
        행추가
      </MDButton>

      <MDButton variant="gradient" color="info" onClick={onSave}>
        저장
      </MDButton>
    </MDBox>
  );
});

// ✅ 직원파출관리 모달의 거래처 검색도 별도 컴포넌트로 분리해 입력 지연을 줄임
const DispatchAccountAutocomplete = React.memo(function DispatchAccountAutocomplete({
  accountOptions,
  selectedOption,
  onChange,
}) {
  const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");

  useEffect(() => {
    setInputValue(selectedOption?.label ?? "");
  }, [selectedOption?.label, selectedOption?.value]);

  // 직원파출관리 모달 Enter 검색
  const selectDispatchAccountByInput = useCallback(() => {
    const q = String(inputValue || "").trim();
    if (!q) return;

    const qLower = q.toLowerCase();
    const exact = accountOptions.find((option) => String(option?.label ?? "").toLowerCase() === qLower);
    const partial =
      exact ||
      accountOptions.find((option) =>
        String(option?.label ?? "")
          .toLowerCase()
          .includes(qLower)
      );

    if (partial) {
      setInputValue(partial.label || q);
      onChange(partial);
    }
  }, [accountOptions, inputValue, onChange]);

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 220 }}
      options={accountOptions}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, option) => {
        if (!option) return;
        onChange(option);
      }}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={filterOptionsByLabel}
      renderInput={(params) => (
        <TextField
          {...params}
          label="거래처"
          placeholder="거래처 검색"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              selectDispatchAccountByInput();
              // Enter 검색 직후 입력 포커스 해제
              if (typeof e.target?.blur === "function") {
                e.target.blur();
              }
            }
          }}
          sx={{
            "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                  "& .MuiInputLabel-root": { fontSize: 12 },
            "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
          }}
        />
      )}
    />
  );
});

function AccountMemberSheet() {
  // =========================
  // ✅ 통합이면 account_id=1 강제
  // =========================
  const INTEGRATION_POSITION = "7";
  const INTEGRATION_ACCOUNT_ID = "1";

  // =========================
  // ✅ 유틸이면 account_id=2 강제
  // =========================
  const UTIL_POSITION = "6";
  const UTIL_ACCOUNT_ID = "2";

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [memberSearchName, setMemberSearchName] = useState("");
  const [activeStatus, setActiveStatus] = useState("N");
  const accountInitRef = useRef(false);
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletOrSmallDesktop = useMediaQuery("(max-width:1280px)");
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [maskingEnabled, setMaskingEnabled] = useState(true);

  const {
    activeRows,
    setActiveRows,
    originalRows,
    setOriginalRows,
    accountList,
    workSystemList, // ✅ 추가
    originalWorkSystemList, // ✅ 추가
    fetchWorkSystemList, // ✅ 추가
    saveWorkSystemList, // ✅ 추가
    deleteDispatchMappingRow, // ✅ 추가
    fetchAccountMembersAllList,
    loading: hookLoading,
  } = useAccountMembersheetData(selectedAccountId, activeStatus, memberSearchName);

  const [loading, setLoading] = useState(true);
  // 열 너비 고정 상태
  const [isColumnWidthLocked, setIsColumnWidthLocked] = useState(false);
  // 자동 측정 열 폭 캐시
  const [measuredColWidths, setMeasuredColWidths] = useState({});
  // 마스킹 전환 재고정 플래그
  const [relockAfterMeasure, setRelockAfterMeasure] = useState(false);

  const applyUtilAccountId = useCallback(
    (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const pos = String(r?.position_type ?? "");
        const acc = r?.account_id;

        // ✅ 통합이면 무조건 1
        if (pos === INTEGRATION_POSITION) {
          return { ...r, account_id: INTEGRATION_ACCOUNT_ID };
        }

        // ✅ 유틸이면 무조건 2
        if (pos === UTIL_POSITION) {
          return { ...r, account_id: UTIL_ACCOUNT_ID };
        }

        // ✅ 통합이 아닌데 account_id가 1로 남아있으면 선택 거래처로 복구(원하면 제거 가능)
        if (String(acc ?? "") === INTEGRATION_ACCOUNT_ID) {
          return { ...r, account_id: selectedAccountId || (accountList?.[0]?.account_id ?? "") };
        }

        // ✅ 유틸이 아닌데 account_id가 2로 남아있으면 선택 거래처로 복구(원하면 제거 가능)
        if (String(acc ?? "") === UTIL_ACCOUNT_ID) {
          return { ...r, account_id: selectedAccountId || (accountList?.[0]?.account_id ?? "") };
        }

        return r;
      });
    },
    [INTEGRATION_POSITION, INTEGRATION_ACCOUNT_ID, UTIL_POSITION, UTIL_ACCOUNT_ID, selectedAccountId, accountList]
  );

  // =========================
  // ✅ 근무형태 관리 Modal 상태
  // =========================
  const [wsOpen, setWsOpen] = useState(false);
  const [wsRows, setWsRows] = useState([]);
  const [wsOriginal, setWsOriginal] = useState([]);
  // ✅ 근무형태 모달 우클릭 컨텍스트 메뉴 상태
  const [wsCtxMenu, setWsCtxMenu] = useState({ open: false, mouseX: 0, mouseY: 0, rowIndex: null, idx: null });

  const numericCols = ["salary"];

  // =========================
  // ✅ 이미지 업로드/뷰어 기능 (추가)
  // =========================
  const imageFields = ["employment_contract", "id", "bankbook"];
  const previewFieldLabels = {
    employment_contract: "근로계약서",
    id: "신분증",
    bankbook: "통장사본",
  };
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewObjectUrlsRef = useRef([]);
  const fileIconSx = { color: "#1e88e5" };

  const getExt = (p = "") => {
    const clean = String(p).split("?")[0].split("#")[0];
    return clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  };

  const toAbsoluteUrl = (p) => {
    if (!p) return "";
    if (/^https?:\/\//i.test(p)) return p;
    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    let path = String(p);
    if (!path.startsWith("/")) path = `/${path}`;
    if (base.endsWith("/api") && path.startsWith("/api/")) {
      path = path.replace(/^\/api/, "");
    }
    return `${base}${path}`;
  };
  const clearPreviewObjectUrls = useCallback(() => {
    previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewObjectUrlsRef.current = [];
  }, []);

  const getPreviewKind = useCallback(
    (value) =>
      typeof value === "object"
        ? getHeadOfficeDocumentPreviewKind(value)
        : getHeadOfficeDocumentPreviewKind({ image_path: value }),
    []
  );

  const toPreviewUrl = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "object") {
      const objectUrl = URL.createObjectURL(value);
      previewObjectUrlsRef.current.push(objectUrl);
      return objectUrl;
    }
    const path = String(value || "");
    if (/^(https?:\/\/|blob:|data:)/i.test(path)) return path;
    const base = String(API_BASE_URL || "").replace(/\/+$/, "");
    const params = new URLSearchParams();
    params.set("file_path", path.startsWith("/") ? path : `/${path}`);
    return `${base}/Account/AccountStoredFileView?${params.toString()}`;
  }, []);

  const handleViewImage = useCallback(
    (rowData, field) => {
      clearPreviewObjectUrls();
      const files = imageFields
        .map((key) => {
          const value = rowData?.[key];
          if (!value) return null;
          return {
            url: toPreviewUrl(value),
            name: previewFieldLabels[key],
            kind: getPreviewKind(value),
          };
        })
        .filter(Boolean);

      const targetIndex = Math.max(0, files.findIndex((file) => file.name === previewFieldLabels[field]));
      setPreviewFiles(files);
      setPreviewIndex(targetIndex);
    },
    [clearPreviewObjectUrls, getPreviewKind, imageFields, previewFieldLabels, toPreviewUrl]
  );

  const handleCloseViewer = useCallback(() => {
    setPreviewFiles([]);
    setPreviewIndex(0);
    clearPreviewObjectUrls();
  }, [clearPreviewObjectUrls]);

  const handleDownload = useCallback((path) => {
    if (!path || typeof path !== "string") return;
    const url = buildFileDownloadUrl(path);
    const filename = path.split("/").pop() || "download";

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const normalizeTime = (t) => {
    if (!t) return "";
    const raw = String(t).trim();
    if (!raw || raw === "-" || raw.toLowerCase() === "null" || raw.toLowerCase() === "undefined") {
      return "";
    }

    const matched = raw.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
    if (matched) {
      const hh = Number(matched[1]);
      const mm = Number(matched[2]);
      if (Number.isFinite(hh) && Number.isFinite(mm) && hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
        return `${hh}:${String(mm).padStart(2, "0")}`;
      }
    }

    return raw.replace(/^0(\d):/, "$1:");
  };

  const keepEditableTailVisible = useCallback((el) => {
    if (!el) return;
    window.requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, []);

  const lockColumnsForEditing = useCallback(() => {
    setIsColumnWidthLocked((prev) => (prev ? prev : true));
  }, []);

  // hygiene와 동일하게 OperateImgUpload 사용
  const uploadImage = async (file, field, row) => {
    const formData = new FormData();
    formData.append("file", file);

    formData.append("type", "member");
    const gubun = `${field}_${row.member_id || row.rrn || Date.now()}`;
    formData.append("gubun", gubun);
    formData.append("folder", row.account_id || selectedAccountId || "common");

    const res = await api.post("/Operate/OperateImgUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res.data.code === 200) {
      return res.data.image_path;
    }
    throw new Error(res.data.message || "이미지 업로드 실패");
  };
  // =========================

  useEffect(() => {
    if (accountInitRef.current) return;
    if (selectedAccountId) {
      accountInitRef.current = true;
      return;
    }
    if (!Array.isArray(accountList) || accountList.length === 0) return;

    setSelectedAccountId(String(accountList[0].account_id));
    accountInitRef.current = true;
  }, [accountList, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId && !memberSearchName) {
      setLoading(false);
      return;
    }

    setIsColumnWidthLocked(false);
    setLoading(true);
    Promise.resolve(fetchAccountMembersAllList()).finally(() => setLoading(false));
  }, [selectedAccountId, activeStatus, memberSearchName]);

  // 합계 계산
  const calculateTotal = (row) => {
    const breakfast = parseNumber(row.breakfast);
    const lunch = parseNumber(row.lunch);
    const dinner = parseNumber(row.dinner);
    const ceremony = parseNumber(row.ceremony);
    const avgMeals = (breakfast + lunch + dinner) / 3;
    return Math.round(avgMeals + ceremony);
  };

  // ★★★★★ activeRows 변경 시 loading false 제거
  useEffect(() => {
    if (activeRows && activeRows.length > 0) {
      const updated = activeRows.map((row) => ({
        ...row,
        total: calculateTotal(row),
      }));

      const fixed = applyUtilAccountId(updated);
      setActiveRows(fixed);
      setOriginalRows(fixed);
    } else {
      setOriginalRows([]);
    }
  }, [activeRows?.length, applyUtilAccountId]);

  // 시간 옵션
  const generateTimeOptions = (startHHMM, endHHMM, stepMinutes = 30) => {
    const toMinutes = (hhmm) => {
      const [h, m] = hhmm.split(":").map(Number);
      return h * 60 + m;
    };
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const start = toMinutes(startHHMM);
    const end = toMinutes(endHHMM);
    const arr = [];
    for (let t = start; t <= end; t += stepMinutes) {
      const hh = Math.floor(t / 60);
      const mm = t % 60;
      arr.push(`${hh}:${pad(mm)}`);
    }
    return arr;
  };
  const startTimes = generateTimeOptions("5:30", "16:00", 30);
  const endTimes = generateTimeOptions("10:00", "20:00", 30);

  const positionOptions = [
    { value: "1", label: "영양사" },
    { value: "2", label: "조리팀장" },
    { value: "3", label: "조리장" },
    { value: "4", label: "조리사" },
    { value: "5", label: "조리원" },
    { value: "6", label: "유틸" },
    { value: "7", label: "통합" },
  ];

  // ✅ 직책 표시는 position_type 우선, 없으면 서버 문자열(position)로 폴백
  const getPositionLabel = (positionType, positionText) => {
    const key = String(positionType ?? "").trim();
    if (key) {
      return positionOptions.find((p) => String(p.value) === key)?.label ?? key;
    }
    return String(positionText ?? "").trim();
  };

  const contractOptions = [
    { value: "1", label: "4대보험" },
    { value: "2", label: "프리랜서" },
  ];

  const delOptions = [
    { value: "N", label: "재직" },
    { value: "Y", label: "퇴사" },
  ];

  const displayOptions = [
    { value: "Y", label: "표시" },
    { value: "N", label: "숨김" },
  ];

  const corOptions = [
    { value: "1", label: "(주)더채움" },
    { value: "2", label: "더채움" },
  ];

  const workSystemLabelMap = useMemo(() => {
    const m = new Map();
    (workSystemList || []).forEach((w) => {
      const key = String(w.idx ?? w.work_system ?? "");
      if (key) m.set(key, w.work_system ?? "");
    });
    return m;
  }, [workSystemList]);

  const mapLabel = (options, v) => {
    const key = String(v ?? "");
    return options.find((o) => String(o.value) === key)?.label ?? key;
  };

  const formatDateForInput = (val) => {
    if (!val && val !== 0) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const insuranceDateFieldKeys = [
    "national_pension",
    "health_insurance",
    "industrial_insurance",
    "employment_insurance",
  ];

  const getInsuranceStatus = (val) => {
    const raw = String(val ?? "").trim();
    if (!raw) return "";
    if (raw === "미해당" || raw.toUpperCase() === "N") return "N";
    return formatDateForInput(raw) ? "Y" : "N";
  };

  // ✅ 거래처 옵션(Autocomplete)
  const accountOptions = useMemo(
    () =>
      (accountList || []).map((a) => ({
        value: String(a.account_id),
        label: a.account_name,
      })),
    [accountList]
  );


  const buildExcelRows = (rows) =>
    (rows || []).map((r) => ({
      ...r,
      salary: parseNumber(r.salary),
      cor_type: mapLabel(corOptions, r.cor_type),
      contract_type: mapLabel(contractOptions, r.contract_type),
      position_type: mapLabel(positionOptions, r.position_type),
      idx: workSystemLabelMap.get(String(r.idx ?? "")) ?? String(r.idx ?? ""),
    }));

  const handleExcelDownloadAllAccounts = async () => {
    if (excelDownloading) return;
    setExcelDownloading(true);

    try {
      Swal.fire({
        title: "엑셀 생성 중...",
        text: "현장 직원관리 데이터를 조회하고 있습니다.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // ✅ 전체 거래처 엑셀 전용 API 사용 (일반 조회 API와 분리)
      const res = await api.get("/Operate/AccountMemberAllListExcel", {
        params: { del_yn: activeStatus },
      });
      const fetchedRows = (res.data || []).map((item) => ({
        account_id: item.account_id,
        account_name: item.account_name,
        member_id: item.member_id,
        name: item.name,
        rrn: item.rrn,
        position_type: item.position_type,
        account_number: item.account_number,
        phone: item.phone,
        address: item.address,
        contract_type: item.contract_type,
        join_dt: item.join_dt,
        act_join_dt: item.act_join_dt,
        ret_set_dt: item.ret_set_dt,
        loss_major_insurances: item.loss_major_insurances,
        del_yn: item.del_yn,
        display_yn: item.display_yn ?? "Y",
        del_dt: item.del_dt,
        del_note: item.del_note,
        salary: parseNumber(item.salary),
        idx: item.idx,
        start_time: normalizeTime(item.start_time),
        end_time: normalizeTime(item.end_time),
        national_pension: item.national_pension,
        health_insurance: item.health_insurance,
        industrial_insurance: item.industrial_insurance,
        employment_insurance: item.employment_insurance,
        employment_contract: normalizeMemberFilePath(item.employment_contract),
        headoffice_note: item.headoffice_note,
        subsidy: item.subsidy,
        note: item.note,
        id: normalizeMemberFilePath(item.id),
        bankbook: normalizeMemberFilePath(item.bankbook),
        cor_type: item.cor_type,
      }));
      const rows = buildExcelRows(fetchedRows);

      const wb = new ExcelJS.Workbook();
      wb.creator = "AccountMemberSheet";

      const ws = wb.addWorksheet("현장 직원관리");
      const baseCols = columns
        .filter((c) => c.accessorKey)
        .map((c) => ({ header: c.header, key: c.accessorKey, width: 14 }));

      const now = dayjs();
      const y = now.year();
      const m = String(now.month() + 1).padStart(2, "0");
      const d = String(now.date()).padStart(2, "0");
      const todayLabel = now.format("YYYY-MM-DD");
      const accNameMap = new Map();
      (accountList || []).forEach((a) => {
        accNameMap.set(String(a.account_id), a.account_name);
      });

      const addSectionTitle = (title, colCount) => {
        ws.addRow([title]);
        const r = ws.lastRow.number;
        ws.mergeCells(r, 1, r, colCount);
        const cell = ws.getCell(r, 1);
        cell.font = { bold: true, size: 12 };
        cell.alignment = { vertical: "middle", horizontal: "left" };
        ws.getRow(r).height = 26;
      };

      const styleHeaderRow = (rowNum) => {
        const row = ws.getRow(rowNum);
        row.font = { bold: true };
        row.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF0F0F0" },
          };
        });
      };

      const styleDataRow = (rowNum) => {
        const row = ws.getRow(rowNum);
        row.alignment = { vertical: "top", horizontal: "left", wrapText: true };
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      };

      // ✅ 컬럼명 중복 방지: columns에는 header를 쓰지 않고, 아래에서 헤더 row를 직접 추가
      const excelCols = [];
      baseCols.forEach((c) => {
        if (c.key === "cor_type") {
          excelCols.push(c);
          excelCols.push({ header: "업장", key: "account_label", width: 20 });
          return;
        }
        if (c.key === "account_id") return;
        excelCols.push(c);
      });

      ws.columns = excelCols.map((c) => ({ key: c.key, width: c.width }));

      const rowsWithAccount = rows.map((r) => {
        const accId = String(r.account_id ?? "");
        // ✅ 엑셀 전용 API에서 내려준 account_name 우선 사용
        const accName = String(r.account_name ?? "").trim() || accNameMap.get(accId);
        const accountLabel = accName || "";
        return { ...r, account_label: accountLabel };
      });

      // ✅ 엑셀 정렬 우선순위: 유틸/통합 -> 거래처명 -> 성명
      const getUtilIntegrationPriority = (row) => {
        const pos = String(row?.position_type ?? "").trim();
        if (pos === "유틸" || pos === "6") return 0;
        if (pos === "통합" || pos === "7") return 1;
        return 2;
      };

      const rowsForExcel = [...rowsWithAccount].sort((a, b) => {
        const pa = getUtilIntegrationPriority(a);
        const pb = getUtilIntegrationPriority(b);
        if (pa !== pb) return pa - pb;

        const accountCompare = String(a?.account_label ?? "").localeCompare(
          String(b?.account_label ?? ""),
          "ko",
          { sensitivity: "base", numeric: true }
        );
        if (accountCompare !== 0) return accountCompare;

        return String(a?.name ?? "").localeCompare(String(b?.name ?? ""), "ko", {
          sensitivity: "base",
          numeric: true,
        });
      });

      const header = excelCols.map((c) => c.header);
      const autoWidthValues = excelCols.map(() => []);

      addSectionTitle(`■ 현장직원관리 전체 / ${todayLabel}`, excelCols.length);
      ws.addRow(header);
      ws.getRow(ws.lastRow.number).height = 23;
      styleHeaderRow(ws.lastRow.number);
      header.forEach((h, i) => autoWidthValues[i].push(h));

      const salaryColIndex = excelCols.findIndex((c) => c.key === "salary") + 1;

      rowsForExcel.forEach((r) => {
        const row = {};
        excelCols.forEach((c) => {
          if (c.key === "salary") {
            row[c.key] = parseNumber(r[c.key]);
            return;
          }
          row[c.key] = r[c.key] ?? "";
        });
        ws.addRow(row);
        const rowNum = ws.lastRow.number;
        ws.getRow(rowNum).height = 23;
        styleDataRow(rowNum);

        if (salaryColIndex > 0) {
          const salaryCell = ws.getRow(rowNum).getCell(salaryColIndex);
          if (typeof salaryCell.value === "number") {
            salaryCell.numFmt = "#,##0";
            salaryCell.alignment = { vertical: "top", horizontal: "right", wrapText: true };
          }
        }

        excelCols.forEach((c, i) => {
          const value = row[c.key];
          autoWidthValues[i].push(c.key === "salary" ? formatNumber(parseNumber(value)) : value);
        });
      });

      const calcWidth = (values, min = 15, max = 80) => {
        const longest = Math.max(...values.map((v) => String(v ?? "").length), 0);
        return Math.min(Math.max(longest + 2, min), max);
      };
      autoWidthValues.forEach((vals, i) => {
        const key = excelCols[i]?.key;
        const minWidth = key === "display_yn" ? 22 : 15;
        ws.getColumn(i + 1).width = calcWidth(vals, minWidth);
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = `현장직원관리_전체_${y}-${m}-${d}.xlsx`;
      saveAs(blob, filename);

      Swal.fire({ title: "완료", text: "엑셀 다운로드가 완료되었습니다.", icon: "success" });
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "실패", text: "엑셀 생성 중 오류가 발생했습니다.", icon: "error" });
    } finally {
      setExcelDownloading(false);
    }
  };

  const handleToolbarAccountSelect = useCallback((accountId) => {
    setLoading(true);
    setMemberSearchName("");
    setSelectedAccountId(accountId);
  }, []);

  const handleToolbarMemberSearch = useCallback((name) => {
    setLoading(true);
    setMemberSearchName(name);
  }, []);

  const handleActiveStatusChange = useCallback((status) => {
    setLoading(true);
    setActiveStatus(status);
  }, []);

  const handleToggleMasking = useCallback(() => {
    setMaskingEnabled((prev) => !prev);
    if (isColumnWidthLocked) {
      setRelockAfterMeasure(true);
      setIsColumnWidthLocked(false);
    }
  }, [isColumnWidthLocked]);

  const columns = useMemo(
    () => [
      { header: "구분", accessorKey: "cor_type", size: 100 },
      { header: "성명", accessorKey: "name", size: 100 },
      { header: "주민번호", accessorKey: "rrn", size: 100 },
      { header: "업장명", accessorKey: "account_id", size: 150 },
      { header: "직책", accessorKey: "position_type", size: 100 },
      { header: "계좌번호", accessorKey: "account_number", size: 150 },
      { header: "연락처", accessorKey: "phone", size: 100 },
      { header: "주소", accessorKey: "address", size: 150 },
      { header: "계약형태", accessorKey: "contract_type", size: 50 },
      { header: "실입사일", accessorKey: "act_join_dt", size: 80 },
      { header: "입사일", accessorKey: "join_dt", size: 80 },
      { header: "퇴직정산일", accessorKey: "ret_set_dt", size: 80 },
      { header: "4대보험 상실일", accessorKey: "loss_major_insurances", size: 80 },
      { header: "퇴사여부", accessorKey: "del_yn", size: 80 },
      { header: "출근부 표시 여부", accessorKey: "display_yn", size: 90 },
      { header: "퇴사일", accessorKey: "del_dt", size: 80 },
      { header: "퇴사사유", accessorKey: "del_note", size: 100 },
      {
        header: "급여(월)",
        accessorKey: "salary",
        size: 80,
        cell: (info) => formatNumber(info.getValue()),
      },
      { header: "근무형태", accessorKey: "idx", size: 180 },
      { header: "시작", accessorKey: "start_time", size: 60 },
      { header: "마감", accessorKey: "end_time", size: 60 },
      { header: "국민연금", accessorKey: "national_pension", size: 80 },
      { header: "건강보험", accessorKey: "health_insurance", size: 80 },
      { header: "산재보험", accessorKey: "industrial_insurance", size: 80 },
      { header: "고용보험", accessorKey: "employment_insurance", size: 80 },
      { header: "비고", accessorKey: "note", minWidth: 80, maxWidth: 150 },
      { header: "본사노트", accessorKey: "headoffice_note", minWidth: 80, maxWidth: 150 },
      { header: "지원금", accessorKey: "subsidy", minWidth: 80, maxWidth: 150 },

      // ✅ 이미지 컬럼 3개 추가 (RecSheet와 동일 항목명)
      { header: "근로계약서", accessorKey: "employment_contract", size: 120 },
      { header: "신분증", accessorKey: "id", size: 120 },
      { header: "통장사본", accessorKey: "bankbook", size: 120 },
    ],
    []
  );

  const table = useReactTable({
    data: activeRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (loading || isColumnWidthLocked) return;

    const rafId = window.requestAnimationFrame(() => {
      const tableEl = tableContainerRef.current?.querySelector("table");
      if (!tableEl) return;

      const headerCells = tableEl.querySelectorAll("thead th");
      if (!headerCells || headerCells.length === 0) return;

      const next = {};
      table.getFlatHeaders().forEach((header, idx) => {
        const el = headerCells[idx];
        if (!el) return;
        // 고정 열 위치가 실제 경계와 맞도록 소수점 너비 그대로 저장 (잠금 시에만 올림)
        const width = el.getBoundingClientRect().width;
        if (width > 0) next[header.id] = width;
      });

      if (Object.keys(next).length > 0) {
        setMeasuredColWidths(next);
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [loading, isColumnWidthLocked, table, activeRows, maskingEnabled]);

  useEffect(() => {
    if (!relockAfterMeasure || isColumnWidthLocked) return;
    if (Object.keys(measuredColWidths || {}).length === 0) return;

    setIsColumnWidthLocked(true);
    setRelockAfterMeasure(false);
  }, [relockAfterMeasure, isColumnWidthLocked, measuredColWidths]);

  const handleSave = async () => {
    handleCloseViewer();
    const changedRowsWithIndex = activeRows
      .map((row, idx) => ({ row, rowIndex: idx }))
      .filter(({ row, rowIndex }) => {
        const original = originalRows[rowIndex];
        if (!original) return true;

        return Object.keys(row).some((key) => {
          // 이미지 필드는 파일 객체로 바뀌면 저장 대상에 포함한다.
          if (imageFields.includes(key)) {
            const v = row[key];
            const o = original[key];
            if (typeof v === "object" && v) return true;
            return String(v ?? "") !== String(o ?? "");
          }

          if (numericCols.includes(key)) {
            return Number(row[key] ?? 0) !== Number(original[key] ?? 0);
          }
          return String(row[key] ?? "") !== String(original[key] ?? "");
        });
      });
    const changedRows = changedRowsWithIndex.map((item) => item.row);

    if (changedRows.length === 0) {
      Swal.fire("저장할 변경사항이 없습니다.", "", "info");
      return;
    }

    try {
      const userId = localStorage.getItem("user_id");

      // 빈 문자열은 백엔드 저장값 기준에 맞춰 null로 변환한다.
      const cleanRow = (row) => {
        const newRow = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (newRow[key] === "" || newRow[key] === undefined) {
            newRow[key] = null;
          }
        });
        return newRow;
      };

      // 첨부 파일은 저장 시 업로드하고 행 값은 서버 경로로 치환한다.
      const processedWithIndex = await Promise.all(
        changedRowsWithIndex.map(async ({ row, rowIndex }) => {
          const newRow = cleanRow(row);

          for (const field of imageFields) {
            if (newRow[field] && typeof newRow[field] === "object") {
              const uploadedPath = await uploadImage(newRow[field], field, newRow);
              newRow[field] = uploadedPath;
            }
          }

          return {
            rowIndex,
            row: {
              ...newRow,
              user_id: userId,
            },
          };
        })
      );

      const processedFixed = applyUtilAccountId(processedWithIndex.map((item) => item.row));

      // 통합/유틸 account_id 보정값을 실제 저장 payload에도 반영한다.
      const res = await api.post("/Operate/AccountMembersSave", {
        data: processedFixed,
      });

      if (res.data.code === 200) {
        Swal.fire("저장 완료", "변경사항이 저장되었습니다.", "success");
        const savedRowByIndex = new Map(
          processedWithIndex.map((item, idx) => [item.rowIndex, processedFixed[idx]])
        );
        const nextRows = applyUtilAccountId(
          activeRows.map((row, idx) => (savedRowByIndex.has(idx) ? { ...row, ...savedRowByIndex.get(idx) } : row))
        );
        setActiveRows(nextRows);
        setOriginalRows(nextRows.map((row) => ({ ...row })));
        await fetchAccountMembersAllList();
        setIsColumnWidthLocked(false);
      } else {
        Swal.fire("저장 실패", res.data.message || "서버 오류", "error");
      }
    } catch (err) {
      Swal.fire("저장 실패", err.message, "error");
    }
  };

  // =========================
  // ✅ 근무형태 모달 로직
  // =========================
  const openWorkSystemModal = async () => {
    const latest = await fetchWorkSystemList({ snapshot: true });

    setWsRows(latest || []);
    setWsOriginal(latest || []);
    setWsOpen(true);
  };

  const closeWorkSystemModal = () => {
    setWsOpen(false);
  };

  const handleWsAddRow = () => {
    const newRow = {
      idx: null,
      work_system: "",
      start_time: startTimes?.[0] ?? "5:30",
      end_time: endTimes?.[0] ?? "10:00",
    };
    setWsRows((prev) => [newRow, ...prev]);
    setWsOriginal((prev) => [newRow, ...prev]);
  };

  const handleWsChange = (rowIndex, key, value) => {
    setWsRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [key]: value } : r)));
  };

  const getWsChangedRows = () => {
    const norm = (v) => String(v ?? "");
    return wsRows.filter((r, i) => {
      const o = wsOriginal[i];
      if (!o) return true;

      return (
        norm(r.work_system) !== norm(o.work_system) ||
        norm(r.start_time) !== norm(o.start_time) ||
        norm(r.end_time) !== norm(o.end_time)
      );
    });
  };

  const handleWsSave = async () => {
    const changed = getWsChangedRows();

    if (changed.length === 0) {
      Swal.fire("저장할 변경사항이 없습니다.", "", "info");
      return;
    }

    try {
      const res = await saveWorkSystemList(changed);
      const ok = res?.status === 200 || res?.data?.code === 200;

      if (!ok) {
        Swal.fire("저장 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("저장 완료", "근무형태가 저장되었습니다.", "success");

      const latest = await fetchWorkSystemList({ snapshot: true });
      setWsRows(latest || []);
      setWsOriginal(latest || []);

      setWsOpen(false);
    } catch (err) {
      Swal.fire("저장 실패", err?.message || "오류", "error");
    }
  };

  const handleWsContextMenu = (e, rowIndex, idx) => {
    e.preventDefault();
    setWsCtxMenu({ open: true, mouseX: e.clientX, mouseY: e.clientY, rowIndex, idx });
  };

  const closeWsCtxMenu = () => setWsCtxMenu({ open: false, mouseX: 0, mouseY: 0, rowIndex: null, idx: null });

  const handleWsDeleteRow = async () => {
    const { rowIndex, idx } = wsCtxMenu;
    closeWsCtxMenu();

    if (idx == null) {
      setWsRows((prev) => prev.filter((_, i) => i !== rowIndex));
      setWsOriginal((prev) => prev.filter((_, i) => i !== rowIndex));
      return;
    }

    const confirm = await Swal.fire({
      title: "행 삭제",
      text: "해당 근무형태를 삭제하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await api.post("/Operate/AccountMemberWorkSystemDelete", { idx });
      const ok = res?.data?.code === 200;
      if (!ok) {
        Swal.fire("삭제 실패", res?.data?.message || "서버 오류", "error");
        return;
      }
      const latest = await fetchWorkSystemList({ snapshot: true });
      setWsRows(latest || []);
      setWsOriginal(latest || []);
    } catch (err) {
      Swal.fire("삭제 실패", err?.message || "오류", "error");
    }
  };

  // =========================
  // ✅ 통합/유틸 관리 모달 로직 (추가)
  // =========================
  const [utilOpen, setUtilOpen] = useState(false);

  // 왼쪽: /Account/AccountUtilMemberList (member_id, position_type)
  const [utilMemberRows, setUtilMemberRows] = useState([]);
  const [utilSelectedMember, setUtilSelectedMember] = useState(null);

  // 가운데: /Account/AccountUtilMappingList (member_id로 조회) -> idx, account_id, member_id, name, position_type
  const [utilMappingRows, setUtilMappingRows] = useState([]);
  const [utilSelectedMappingRowIndex, setUtilSelectedMappingRowIndex] = useState(null); // (추후 삭제 기능에 쓸 수 있음)
  // ✅ 매핑 목록(가운데) 우클릭 컨텍스트 메뉴 상태 (직책이 통합일 때만 "삭제 처리" 노출)
  const [utilMappingCtxMenu, setUtilMappingCtxMenu] = useState({
    open: false,
    mouseX: 0,
    mouseY: 0,
    rowIndex: null,
  });

  // 오른쪽: /Account/AccountList -> account_id, account_name
  const [utilAccountRows, setUtilAccountRows] = useState([]);
  const [utilSelectedAccount, setUtilSelectedAccount] = useState(null);

  const fetchUtilMemberList = useCallback(async () => {
    const res = await api.get("/Account/AccountUtilMemberList");
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const fetchUtilMappingList = useCallback(async (memberId) => {
    if (!memberId) return [];
    const res = await api.get("/Account/AccountUtilMappingList", {
      params: { member_id: memberId },
    });
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const fetchUtilAccountList = useCallback(async () => {
    const res = await api.get("/Account/AccountList", {
      params: { account_type: 0 },
    });
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const openUtilModal = async () => {
    try {
      // 모달 초기화
      setUtilSelectedMember(null);
      setUtilSelectedAccount(null);
      setUtilSelectedMappingRowIndex(null);
      setUtilMappingRows([]);

      const [members, accounts] = await Promise.all([
        fetchUtilMemberList(),
        fetchUtilAccountList(),
      ]);
      setUtilMemberRows(members || []);
      setUtilAccountRows(accounts || []);

      setUtilOpen(true);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const closeUtilModal = () => {
    setUtilOpen(false);
  };

  const handleSelectUtilMember = async (row) => {
    try {
      setUtilSelectedMember(row);
      setUtilSelectedMappingRowIndex(null);

      const memberId = row?.member_id;
      if (!memberId) {
        setUtilMappingRows([]);
        return;
      }

      const mappings = await fetchUtilMappingList(memberId);
      setUtilMappingRows(mappings || []);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const handleAddMappingFromRight = () => {
    if (!utilSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합/유틸 직원을 먼저 선택해주세요.", "info");
      return;
    }
    if (!utilSelectedAccount?.account_id) {
      Swal.fire("안내", "오른쪽에서 업장을 먼저 선택해주세요.", "info");
      return;
    }

    const member_id = utilSelectedMember.member_id;
    const position_type = utilSelectedMember.position_type;
    const account_id = utilSelectedAccount.account_id;

    // 중복 방지: 같은 member_id + account_id 이미 있으면 추가 안함
    const exists = (utilMappingRows || []).some(
      (r) =>
        String(r.member_id) === String(member_id) && String(r.account_id) === String(account_id)
    );
    if (exists) {
      Swal.fire("안내", "이미 매핑된 업장입니다.", "info");
      return;
    }

    const newRow = {
      idx: null, // 신규
      account_id,
      member_id,
      name: utilSelectedMember.name ?? utilSelectedMember.member_name ?? "",
      position_type,
    };

    setUtilMappingRows((prev) => [newRow, ...(prev || [])]);
  };

  const handleUtilSave = async () => {
    if (!utilSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합/유틸 직원을 먼저 선택해주세요.", "info");
      return;
    }

    try {
      // 서버 스펙이 명확하지 않아서, member_id + data 함께 전송 (대부분 이 형태로 처리 가능)
      const payload = {
        member_id: utilSelectedMember.member_id,
        data: utilMappingRows || [],
      };

      const res = await api.post("/Account/AccountUtilMemberMappingSave", utilMappingRows);
      const ok = res?.status === 200 || res?.data?.code === 200;

      if (!ok) {
        Swal.fire("저장 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("저장 완료", "통합/유틸 매핑이 저장되었습니다.", "success");

      // 저장 후 가운데 재조회
      const latest = await fetchUtilMappingList(utilSelectedMember.member_id);
      setUtilMappingRows(latest || []);
      setUtilOpen(false);
    } catch (err) {
      Swal.fire("저장 실패", err?.message || "오류", "error");
    }
  };

  // ✅ 매핑 목록(가운데) 우클릭 메뉴 열기/닫기 (직책 통합(7)인 행에서만 사용)
  const handleUtilMappingContextMenu = useCallback((e, rowIndex) => {
    const row = utilMappingRows?.[rowIndex];
    if (String(row?.position_type ?? "") !== INTEGRATION_POSITION) return;
    e.preventDefault();
    e.stopPropagation();
    setUtilMappingCtxMenu({ open: true, mouseX: e.clientX, mouseY: e.clientY, rowIndex });
  }, [utilMappingRows]);

  const closeUtilMappingCtxMenu = useCallback(() => {
    setUtilMappingCtxMenu((prev) => ({ ...prev, open: false, rowIndex: null }));
  }, []);

  // ✅ 통합/유틸 매핑 삭제 처리: tb_account_util_member_mapping.del_yn만 'Y'로 변경(직원 원본은 유지)
  const handleUtilMappingDelete = useCallback(async () => {
    const { rowIndex } = utilMappingCtxMenu;
    const targetRow = utilMappingRows?.[rowIndex];
    closeUtilMappingCtxMenu();
    if (!targetRow) return;

    // 아직 저장 안 된 신규 매핑 행은 로컬에서만 제거
    if (targetRow.idx == null) {
      setUtilMappingRows((prev) => prev.filter((_, i) => i !== rowIndex));
      return;
    }

    const confirm = await Swal.fire({
      title: "삭제 처리",
      text: "해당 매핑을 삭제 처리(제외) 하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제 처리",
      cancelButtonText: "취소",
    });
    if (!confirm.isConfirmed) return;

    try {
      const userId = localStorage.getItem("user_id");
      const res = await api.post("/Account/AccountUtilMemberMappingSave", [
        {
          idx: targetRow.idx,
          member_id: targetRow.member_id,
          account_id: targetRow.account_id,
          del_yn: "Y",
          user_id: userId,
        },
      ]);
      const ok = res?.status === 200 || res?.data?.code === 200;
      if (!ok) {
        Swal.fire("처리 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("완료", "삭제 처리되었습니다.", "success");
      const latest = await fetchUtilMappingList(targetRow.member_id);
      setUtilMappingRows(latest || []);
    } catch (err) {
      Swal.fire("처리 실패", err?.message || "오류", "error");
    }
  }, [utilMappingCtxMenu, utilMappingRows, closeUtilMappingCtxMenu, fetchUtilMappingList]);

  // =========================
  // ✅ 직원파출관리 모달 로직 (통합/유틸 관리 복제 → "파견일자"로 변경)
  // =========================
  const DISPATCH_TYPE = 6;

  const [dispatchAccountId, setDispatchAccountId] = useState("");

  const selectedDispatchAccountOption = useMemo(() => {
    const v = String(dispatchAccountId ?? "");
    return accountOptions.find((o) => o.value === v) || null;
  }, [accountOptions, dispatchAccountId]);

  const [dispatchOpen, setDispatchOpen] = useState(false);

  // 왼쪽: 직원 목록
  const [dispatchMemberRows, setDispatchMemberRows] = useState([]);
  const [dispatchSelectedMember, setDispatchSelectedMember] = useState(null);

  // 가운데: 매핑 목록
  const [dispatchMappingRows, setDispatchMappingRows] = useState([]);
  const [dispatchOriginalMappingRows, setDispatchOriginalMappingRows] = useState([]);
  const [dispatchSelectedMappingRowIndex, setDispatchSelectedMappingRowIndex] = useState(null);
  const [dispatchCtxMenu, setDispatchCtxMenu] = useState({
    open: false,
    mouseX: 0,
    mouseY: 0,
    rowIndex: null,
  });
  const [dispatchSaving, setDispatchSaving] = useState(false);
  const dispatchSaveLockRef = useRef(false);

  // 오른쪽: 업장 목록
  const [dispatchAccountRows, setDispatchAccountRows] = useState([]);
  const [dispatchSelectedAccount, setDispatchSelectedAccount] = useState(null);

  // ✅ record_date → record_year / record_month / record_date(YYYY-MM-DD) 분리
  const splitRecordDate = (dateStr) => {
    const d = dayjs(dateStr);
    if (!dateStr || !d.isValid()) {
      return { record_year: null, record_month: null, record_date: null };
    }
    return {
      record_year: d.year(),
      record_month: d.month() + 1,
      record_date: d.date(),
      //record_date: d.format("YYYY-MM-DD"),
    };
  };

  const normalizeDispatchDate = useCallback((dateStr) => {
    const raw = String(dateStr ?? "").trim();
    if (!raw) return "";
    const d = dayjs(raw);
    return d.isValid() ? d.format("YYYY-MM-DD") : raw;
  }, []);

  const buildDispatchDuplicateKey = (row) => {
    const ownerAccountId = String(row?.account_id ?? "").trim();
    const dispatchAccId = String(row?.dispatch_account_id ?? "").trim();
    const { record_year, record_month, record_date } = splitRecordDate(row?.record_date);
    if (!ownerAccountId || !dispatchAccId || !record_year || !record_month || !record_date) return "";
    // ✅ 저장 payload와 동일하게 년/월/일 숫자 기준으로 중복키를 생성
    return [ownerAccountId, dispatchAccId, record_year, record_month, record_date].join("|");
  };

  // ✅ 가운데 테이블에서 파견일자 수정용
  const handleDispatchRowChange = (rowIndex, key, value) => {
    setDispatchMappingRows((prev) =>
      (prev || []).map((r, i) => (i === rowIndex ? { ...r, [key]: value } : r))
    );
  };

  // ✅ 가운데 매핑 목록 우클릭 메뉴
  const closeDispatchCtxMenu = useCallback(() => {
    setDispatchCtxMenu((prev) => ({ ...prev, open: false, rowIndex: null }));
  }, []);

  const handleDispatchMappingContextMenu = useCallback((e, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();

    setDispatchSelectedMappingRowIndex(rowIndex);
    setDispatchCtxMenu({
      open: true,
      mouseX: e.clientX,
      mouseY: e.clientY,
      rowIndex,
    });
  }, []);

  // ✅ (중요) API는 통합/유틸 관리와 동일하게 호출하되, year/month를 params에 포함
  //    서버가 year/month를 아직 안 받더라도 무시될 수 있으니 안전함
  // ✅ 왼쪽: 직원 목록 (/Operate/AccountMemberAllList)
  // params: account_id, del_yn='N'
  const fetchDispatchMemberList = useCallback(
    async (accountIdParam) => {
      const accId = String(accountIdParam ?? dispatchAccountId ?? "");
      if (!accId) return [];

      const res = await api.get("/Operate/AccountMemberAllList", {
        params: { account_id: accId, del_yn: "N" },
      });

      const list = res?.data?.data ?? res?.data ?? [];
      return Array.isArray(list) ? list : [];
    },
    [dispatchAccountId]
  );

  // ✅ 가운데: 매핑 조회 (/Account/AccountMemberDispatchMappingList)
  // member_id + del_yn='N' 조건으로 조회(삭제처리된 데이터 제외)
  const fetchDispatchMappingList = useCallback(async (memberId) => {
    if (!memberId) return [];

    const res = await api.get("/Account/AccountMemberDispatchMappingList", {
      params: { member_id: memberId, del_yn: "N" },
    });

    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  // ✅ 오른쪽: 업장 목록 (/Account/AccountList)
  const fetchDispatchAccountList = useCallback(async () => {
    const res = await api.get("/Account/AccountList", {
      params: { account_type: 0 },
    });
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  // ✅ 매핑 행의 날짜를 숫자 기준 연/월/일과 화면 표시용 문자열로 함께 정규화
  const buildDispatchRecordDateInfo = useCallback((row) => {
    const rawDate = String(row?.record_date ?? row?.recordDate ?? "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      const parsed = dayjs(rawDate);
      if (parsed.isValid()) {
        return {
          record_year: parsed.year(),
          record_month: parsed.month() + 1,
          record_date: parsed.date(),
          display_date: parsed.format("YYYY-MM-DD"),
        };
      }
    }

    const record_year = Number(row?.record_year ?? row?.recordYear ?? NaN);
    const record_month = Number(row?.record_month ?? row?.recordMonth ?? NaN);
    const record_date = Number(row?.record_day ?? row?.recordDay ?? row?.day ?? row?.date ?? row?.record_date);

    if (
      Number.isFinite(record_year) &&
      Number.isFinite(record_month) &&
      Number.isFinite(record_date) &&
      record_month >= 1 &&
      record_month <= 12 &&
      record_date >= 1 &&
      record_date <= 31
    ) {
      return {
        record_year,
        record_month,
        record_date,
        display_date: `${String(record_year).padStart(4, "0")}-${String(record_month).padStart(
          2,
          "0"
        )}-${String(record_date).padStart(2, "0")}`,
      };
    }

    return {
      record_year: null,
      record_month: null,
      record_date: null,
      display_date: normalizeDispatchDate(rawDate),
    };
  }, [normalizeDispatchDate]);

  // ✅ 매핑 조회 SQL에서 tb_account_record 시간을 이미 붙여주므로
  //    화면에서는 날짜/타입만 정리해서 바로 사용
  const normalizeDispatchMappings = useCallback(
    async (mappings, memberRow) => {
      const list = Array.isArray(mappings) ? mappings : [];
      if (list.length === 0) return [];

      return list.map((row) => {
        const dateInfo = buildDispatchRecordDateInfo(row);
        const hasIdx = row?.idx != null && String(row.idx).trim() !== "";
        const fallbackStartTime = hasIdx ? "" : memberRow?.start_time ?? "";
        const fallbackEndTime = hasIdx ? "" : memberRow?.end_time ?? "";

        return {
          ...row,
          start_time: normalizeTime(row?.start_time ?? fallbackStartTime),
          end_time: normalizeTime(row?.end_time ?? fallbackEndTime),
          record_date: dateInfo.display_date || "",
          // ✅ 직원파출관리 모달의 근무기록 타입은 항상 6(직원파출)로 고정
          type: DISPATCH_TYPE,
        };
      });
    },
    [DISPATCH_TYPE, buildDispatchRecordDateInfo]
  );

  const handleDispatchAccountSelect = useCallback(
    async (option) => {
      if (!option) return;

      setDispatchAccountId(option.value);
      setDispatchSelectedMember(null);
      setDispatchSelectedAccount(null);
      setDispatchSelectedMappingRowIndex(null);
      setDispatchMappingRows([]);
      setDispatchOriginalMappingRows([]);

      try {
        const members = await fetchDispatchMemberList(option.value);
        setDispatchMemberRows(members || []);
      } catch (e) {
        Swal.fire("조회 실패", e?.message || "오류", "error");
      }
    },
    [fetchDispatchMemberList]
  );

  const openDispatchModal = async () => {
    try {
      setDispatchSelectedMember(null);
      setDispatchSelectedAccount(null);
      setDispatchSelectedMappingRowIndex(null);
      setDispatchMappingRows([]);
      setDispatchOriginalMappingRows([]);

      const initAccId =
        String(selectedAccountId ?? "") || String(accountOptions?.[0]?.value ?? "") || "";

      setDispatchAccountId(initAccId);

      const [members, accounts] = await Promise.all([
        fetchDispatchMemberList(initAccId),
        fetchDispatchAccountList(),
      ]);

      setDispatchMemberRows(members || []);
      setDispatchAccountRows(accounts || []);

      setDispatchOpen(true);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const closeDispatchModal = () => {
    setDispatchOpen(false);
    closeDispatchCtxMenu();
  };

  // ✅ 직원파출 시간 저장/삭제를 위해 매핑 행을 tb_account_record 저장 형식으로 변환
  const buildDispatchRecordPayloadRow = useCallback(
    (row, originalRow = null, overrideType = DISPATCH_TYPE) => {
      const dateInfo = buildDispatchRecordDateInfo(row);
      const originalDateInfo = originalRow ? buildDispatchRecordDateInfo(originalRow) : dateInfo;
      const memberId = String(
        row?.member_id ?? row?.memberId ?? originalRow?.member_id ?? originalRow?.memberId ?? dispatchSelectedMember?.member_id ?? ""
      ).trim();
      const dispatchAccountIdValue = String(
        row?.dispatch_account_id ??
        row?.dispatchAccountId ??
        originalRow?.dispatch_account_id ??
        originalRow?.dispatchAccountId ??
        ""
      ).trim();
      const positionType = String(
        row?.position_type ?? originalRow?.position_type ?? dispatchSelectedMember?.position_type ?? ""
      ).trim();
      const hasIdx =
        (row?.idx != null && String(row.idx).trim() !== "") ||
        (originalRow?.idx != null && String(originalRow.idx).trim() !== "");
      const defaultStartTime = hasIdx ? "" : normalizeTime(dispatchSelectedMember?.start_time ?? "");
      const defaultEndTime = hasIdx ? "" : normalizeTime(dispatchSelectedMember?.end_time ?? "");
      const originalStartTime = normalizeTime(originalRow?.start_time ?? row?.start_time ?? defaultStartTime);
      const originalEndTime = normalizeTime(originalRow?.end_time ?? row?.end_time ?? defaultEndTime);

      if (
        !memberId ||
        !dispatchAccountIdValue ||
        !dateInfo.record_year ||
        !dateInfo.record_month ||
        !dateInfo.record_date
      ) {
        return null;
      }

      return {
        gubun: String(dispatchSelectedMember?.gubun ?? "nor").trim() || "nor",
        account_id: dispatchAccountIdValue,
        member_id: memberId,
        position_type: positionType,
        positionType: positionType,
        record_year: dateInfo.record_year,
        record_month: dateInfo.record_month,
        record_date: dateInfo.record_date,
        type: Number(overrideType),
        is_present: Number(overrideType) === 0 ? "N" : "Y",
        start_time: Number(overrideType) === 0 ? "" : normalizeTime(row?.start_time ?? defaultStartTime),
        end_time: Number(overrideType) === 0 ? "" : normalizeTime(row?.end_time ?? defaultEndTime),
        salary: Number.isFinite(Number(row?.salary)) ? Number(row.salary) : 0,
        note: null,
        position: row?.position ?? originalRow?.position ?? dispatchSelectedMember?.position ?? "",
        org_start_time: originalStartTime,
        org_end_time: originalEndTime,
        old_account_id: String(
          originalRow?.dispatch_account_id ??
          originalRow?.dispatchAccountId ??
          originalRow?.account_id ??
          originalRow?.accountId ??
          dispatchAccountIdValue
        ).trim(),
        old_member_id: String(originalRow?.member_id ?? originalRow?.memberId ?? memberId).trim(),
        old_record_year: originalDateInfo.record_year ?? dateInfo.record_year,
        old_record_month: originalDateInfo.record_month ?? dateInfo.record_month,
        old_record_date: originalDateInfo.record_date ?? dateInfo.record_date,
        old_type: originalRow ? Number(originalRow?.type ?? DISPATCH_TYPE) : null,
      };
    },
    [DISPATCH_TYPE, buildDispatchRecordDateInfo, dispatchSelectedMember]
  );

  // ✅ 매핑 삭제 시 같은 일자의 tb_account_record 직원파출 시간도 같이 비움
  const clearDispatchRecord = useCallback(
    async (row) => {
      if (!row || row?.idx == null) return;

      const originalRow =
        (dispatchOriginalMappingRows || []).find((item) => String(item?.idx ?? "") === String(row?.idx ?? "")) ||
        row;
      const recordRow = buildDispatchRecordPayloadRow(originalRow, originalRow, 0);
      if (!recordRow) return;

      const res = await api.post("/Account/AccountRecordSave", {
        normalRecords: [recordRow],
        disRecords: [],
        recRecords: [],
      });
      const ok = res?.status === 200 || Number(res?.data?.code) === 200;
      if (!ok) {
        throw new Error(res?.data?.message || "직원파출 시간 삭제에 실패했습니다.");
      }
    },
    [buildDispatchRecordPayloadRow, dispatchOriginalMappingRows]
  );

  // 가운데 매핑 목록 삭제
  const handleDeleteDispatchMappingRow = useCallback(
    async (rowIndex) => {
      if (rowIndex == null) return;
      const targetRow = dispatchMappingRows?.[rowIndex];
      if (!targetRow) return;

      const result = await Swal.fire({
        title: "행 삭제",
        text: "선택한 파출 매핑을 삭제할까요?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#9e9e9e",
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
      });
      if (!result.isConfirmed) return;

      try {
        await clearDispatchRecord(targetRow);
        await deleteDispatchMappingRow(targetRow);

        setDispatchMappingRows((prev) => prev.filter((_, i) => i !== rowIndex));
        setDispatchOriginalMappingRows((prev) => {
          if (targetRow?.idx == null) return prev;
          return (prev || []).filter((r) => String(r.idx ?? "") !== String(targetRow.idx ?? ""));
        });
        setDispatchSelectedMappingRowIndex(null);
        closeDispatchCtxMenu();

        Swal.fire("삭제", "삭제 처리되었습니다.", "success");
      } catch (err) {
        closeDispatchCtxMenu();
        Swal.fire("삭제 실패", err?.message || "오류", "error");
      }
    },
    [clearDispatchRecord, closeDispatchCtxMenu, deleteDispatchMappingRow, dispatchMappingRows]
  );

  const handleSelectDispatchMember = async (row) => {
    try {
      setDispatchSelectedMember(row);
      setDispatchSelectedMappingRowIndex(null);

      const memberId = row?.member_id;
      if (!memberId) {
        setDispatchMappingRows([]);
        setDispatchOriginalMappingRows([]);
        return;
      }

      const mappings = await fetchDispatchMappingList(memberId);
      const normalized = await normalizeDispatchMappings(mappings, row);

      setDispatchMappingRows(normalized);
      setDispatchOriginalMappingRows(normalized);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const handleAddDispatchMappingFromRight = () => {
    if (!dispatchSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 직원을 먼저 선택해주세요.", "info");
      return;
    }
    if (!dispatchSelectedAccount?.account_id) {
      Swal.fire("안내", "오른쪽에서 업장을 먼저 선택해주세요.", "info");
      return;
    }

    const member_id = dispatchSelectedMember.member_id;
    const position_type = dispatchSelectedMember.position_type;

    // ✅ 직원 소속 고객사(직원 원 소속)
    const account_id = dispatchSelectedMember.account_id;

    // ✅ 파견 고객사(업장 목록에서 선택)
    const dispatch_account_id = dispatchSelectedAccount.account_id;

    const newRecordDate = dayjs().format("YYYY-MM-DD");

    const newRow = {
      idx: null, // 신규
      member_id,
      name: dispatchSelectedMember.name ?? dispatchSelectedMember.member_name ?? "",
      position_type,

      account_id,
      dispatch_account_id,

      // ✅ 최초 등록 시에는 tb_account_members(직원 기본시간)으로 시작
      start_time: normalizeTime(dispatchSelectedMember.start_time),
      end_time: normalizeTime(dispatchSelectedMember.end_time),

      // ✅ 파견일자(달력 선택) - 기본값: 오늘
      record_date: newRecordDate,

      // ✅ type 고정
      type: DISPATCH_TYPE,
    };

    setDispatchMappingRows((prev) => [newRow, ...(prev || [])]);
  };

  const handleDispatchSave = async () => {
    if (dispatchSaveLockRef.current || dispatchSaving) return;

    if (!dispatchSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 직원을 먼저 선택해주세요.", "info");
      return;
    }

    // ✅ del_yn='N' 활성 행만 저장 검증 대상으로 사용
    const rowsToValidate = (dispatchMappingRows || []).filter(
      (r) => String(r.del_yn ?? "N").toUpperCase() !== "Y"
    );

    // ✅ 파견일자 필수/유효성 체크
    const invalidDate = rowsToValidate.some((r) => {
      if (!r.record_date) return true;
      const { record_year, record_month, record_date } = splitRecordDate(r.record_date);
      return !record_year || !record_month || !record_date;
    });
    if (invalidDate) {
      Swal.fire("안내", "가운데 매핑 목록에서 파견일자를 올바르게 선택해주세요.", "info");
      return;
    }

    const seenDispatchKeys = new Set();
    const hasDuplicate = rowsToValidate.some((r) => {
      const key = buildDispatchDuplicateKey(r);
      if (!key || seenDispatchKeys.has(key)) return seenDispatchKeys.has(key);
      seenDispatchKeys.add(key);
      return false;
    });
    if (hasDuplicate) {
      Swal.fire(
        "안내",
        "같은 소속고객사/파견고객사/파견일자 조합이 중복되었습니다. 중복 행을 정리해주세요.",
        "info"
      );
      return;
    }

    dispatchSaveLockRef.current = true;
    setDispatchSaving(true);

    try {
      Swal.fire({
        title: "저장중입니다.",
        text: "잠시만 기다려 주세요.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      // ✅ 매핑 저장 payload에도 시간을 같이 실어 보내고,
      //    실제 시간 반영은 tb_account_record 기준으로 처리
      const rowsToSave = (dispatchMappingRows || []).filter(
        (r) => String(r.del_yn ?? "N").toUpperCase() !== "Y"
      );

      if (rowsToSave.length === 0) {
        Swal.close();
        Swal.fire("안내", "저장할 매핑이 없습니다.", "info");
        return;
      }

      const payload = rowsToSave.map((r) => {
        const { record_year, record_month, record_date } = splitRecordDate(r.record_date);
        const hasIdx = r?.idx != null && String(r.idx).trim() !== "";
        const fallbackStartTime = hasIdx ? "" : dispatchSelectedMember.start_time;
        const fallbackEndTime = hasIdx ? "" : dispatchSelectedMember.end_time;

        return {
          // 서버가 idx로 update 구분하면 같이 보내기
          idx: r.idx ?? null,

          member_id: dispatchSelectedMember.member_id,

          // ✅ 소속/파견 고객사
          account_id: r.account_id ?? dispatchSelectedMember.account_id,
          dispatch_account_id: r.dispatch_account_id,

          // ✅ 직원 정보
          name: r.name ?? dispatchSelectedMember.name,
          position_type: r.position_type ?? dispatchSelectedMember.position_type,
          start_time: normalizeTime(r.start_time ?? fallbackStartTime),
          end_time: normalizeTime(r.end_time ?? fallbackEndTime),

          // ✅ 저장용 날짜 3종
          record_year,
          record_month,
          record_date,

          // ✅ type은 6 고정
          type: DISPATCH_TYPE,

          // ✅ 활성 데이터로 저장
          del_yn: "N",
        };
      });

      const res = await api.post("/Account/AccountMemberDispatchMappingSave", payload);
      const ok = res?.status === 200 || res?.data?.code === 200;

      if (!ok) {
        Swal.close();
        Swal.fire("저장 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      // ✅ 백엔드 저장에서 tb_account_member_dispatch_mapping / tb_account_record를 같이 처리하므로
      //    프런트는 저장 후 최신 목록만 다시 읽어 화면을 맞춤
      const latest = await fetchDispatchMappingList(dispatchSelectedMember.member_id);
      const normalized = await normalizeDispatchMappings(latest, dispatchSelectedMember);

      setDispatchMappingRows(normalized);
      setDispatchOriginalMappingRows(normalized);
      Swal.close();
      Swal.fire("저장 완료", "직원 파출 매핑이 저장되었습니다.", "success");
    } catch (err) {
      Swal.close();
      Swal.fire("저장 실패", err?.message || "오류", "error");
    } finally {
      dispatchSaveLockRef.current = false;
      setDispatchSaving(false);
    }
  };

  useEffect(() => {
    if (!dispatchOpen) return;

    (async () => {
      try {
        // 왼쪽 직원목록: 거래처 기준
        if (dispatchAccountId) {
          const members = await fetchDispatchMemberList(dispatchAccountId);
          setDispatchMemberRows(members || []);
        } else {
          setDispatchMemberRows([]);
        }

        // 오른쪽 업장 목록
        const accounts = await fetchDispatchAccountList();
        setDispatchAccountRows(accounts || []);

        // 가운데 매핑(선택된 직원이 있으면)
        if (dispatchSelectedMember?.member_id) {
          const mappings = await fetchDispatchMappingList(dispatchSelectedMember.member_id);
          const normalized = await normalizeDispatchMappings(mappings, dispatchSelectedMember);

          setDispatchMappingRows(normalized);
          setDispatchOriginalMappingRows(normalized);
        } else {
          setDispatchMappingRows([]);
          setDispatchOriginalMappingRows([]);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [
    dispatchOpen,
    dispatchAccountId,
    dispatchSelectedMember?.member_id,
    fetchDispatchMemberList,
    fetchDispatchAccountList,
    fetchDispatchMappingList,
    normalizeDispatchMappings,
  ]);

  useEffect(() => {
    if (!dispatchCtxMenu.open) return;
    const rowIndex = dispatchCtxMenu.rowIndex;
    if (rowIndex == null) return;
    if (rowIndex < (dispatchMappingRows || []).length) return;
    closeDispatchCtxMenu();
  }, [dispatchCtxMenu.open, dispatchCtxMenu.rowIndex, dispatchMappingRows, closeDispatchCtxMenu]);

  // 직원파출관리 모달에서 업장명 표시용 맵
  const dispatchAccountNameMap = useMemo(() => {
    const m = new Map();
    (dispatchAccountRows || []).forEach((a) => {
      m.set(String(a.account_id), a.account_name);
    });
    return m;
  }, [dispatchAccountRows]);

  // ✅ 직원파출관리 모달에서 변경 여부를 즉시 표시하기 위한 원본 행 맵
  const dispatchOriginalMappingRowMap = useMemo(() => {
    const map = new Map();
    (dispatchOriginalMappingRows || []).forEach((row) => {
      if (row?.idx == null) return;
      map.set(String(row.idx), row);
    });
    return map;
  }, [dispatchOriginalMappingRows]);

  // ✅ 직원 소속 고객사(account_id) 이름 맵 (accountOptions 기반)
  const memberAccountNameMap = useMemo(() => {
    const m = new Map();
    (accountOptions || []).forEach((a) => {
      m.set(String(a.value), a.label);
    });
    return m;
  }, [accountOptions]);

  const handleAddRow = () => {
    lockColumnsForEditing();
    const defaultAccountId = selectedAccountId || (accountList?.[0]?.account_id ?? "");
    const defaultWorkSystemIdx = workSystemList?.[0]?.idx ? String(workSystemList[0].idx) : "";

    const defaultPositionType = "1"; // ✅ 문자열로 통일
    const initAccountId =
      defaultPositionType === UTIL_POSITION
        ? UTIL_ACCOUNT_ID
        : defaultPositionType === INTEGRATION_POSITION
          ? INTEGRATION_ACCOUNT_ID
          : String(defaultAccountId);

    const newRow = {
      name: "",
      rrn: "",
      account_id: initAccountId,

      position_type: defaultPositionType,
      account_number: "",
      phone: "",
      address: "",
      contract_type: "1",
      join_dt: "",
      act_join_dt: "",
      ret_set_dt: "",
      loss_major_insurances: "",
      del_yn: activeStatus,
      display_yn: "Y",
      del_dt: "",
      del_note: "",
      salary: "",
      idx: defaultWorkSystemIdx,
      start_time: workSystemList?.[0]?.start_time
        ? normalizeTime(workSystemList[0].start_time)
        : startTimes?.[0] ?? "5:30",
      end_time: workSystemList?.[0]?.end_time
        ? normalizeTime(workSystemList[0].end_time)
        : endTimes?.[0] ?? "10:00",
      national_pension: "",
      health_insurance: "",
      industrial_insurance: "",
      employment_insurance: "",
      note: "",
      headoffice_note: "",
      subsidy: "",
      total: 0,

      employment_contract: "",
      id: "",
      bankbook: "",
      cor_type: "1",
    };

    const fixedRow = applyUtilAccountId([newRow])[0];
    setActiveRows((prev) => [fixedRow, ...(prev || [])]);
    setOriginalRows((prev) => [fixedRow, ...(prev || [])]);
  };

  const renderTable = (table, rows, originals) => {
    const dateFields = new Set([
      "join_dt",
      "act_join_dt",
      "ret_set_dt",
      "loss_major_insurances",
      "del_dt",
    ]);
    const insuranceDateFields = new Set(insuranceDateFieldKeys);
    const selectFields = new Set([
      "position_type",
      "del_yn",
      "display_yn",
      "contract_type",
      "start_time",
      "end_time",
      "account_id",
      "idx",
      "cor_type",
    ]);
    const nonEditableCols = new Set(["diner_date", "total"]);
    // 화면 크기별 고정 열 범위
    // 모바일: 이름(2열)까지 고정, 태블릿: 주민번호(3열)까지 고정, 데스크탑: 기존(7열) 유지
    const stickyColumnCount = isMobile ? 2 : isTablet ? 3 : 7;
    // n번째 고정 열의 left 위치: 앞 열들의 실제 렌더링 너비 합 (측정 전에는 컬럼 size 기준)
    // 너비 잠금 상태에서는 열 너비가 올림값으로 고정되므로 같은 기준으로 합산
    const getStickyLeft = (n) =>
      `${table
        .getFlatHeaders()
        .slice(0, n - 1)
        .reduce((sum, h) => {
          const w = measuredColWidths[h.id] ?? h.column.columnDef.size ?? 80;
          return sum + (isColumnWidthLocked ? Math.ceil(w) : w);
        }, 0)}px`;

    return (
      <MDBox
        ref={tableContainerRef}
        pt={0}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: isMobile ? "55vh" : "75vh",
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          "& table": {
            borderCollapse: "separate",
            width: "max-content",
            minWidth: "100%",
            borderSpacing: 0,
            tableLayout: isColumnWidthLocked ? "fixed" : "auto",
          },
          "& th, & td": {
            border: "1px solid #686D76",
            textAlign: "center",
            padding: "4px",
            whiteSpace: "nowrap",
            fontSize: "12px",
            verticalAlign: "middle",
          },
          "& th": {
            backgroundColor: "#f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 2,
          },
          ...(stickyColumnCount >= 1
            ? {
              "& td:nth-of-type(1), & th:nth-of-type(1)": {
                position: "sticky",
                left: 0,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 2
            ? {
              "& td:nth-of-type(2), & th:nth-of-type(2)": {
                position: "sticky",
                left: getStickyLeft(2),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 3
            ? {
              "& td:nth-of-type(3), & th:nth-of-type(3)": {
                position: "sticky",
                left: getStickyLeft(3),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 4
            ? {
              "& td:nth-of-type(4), & th:nth-of-type(4)": {
                position: "sticky",
                left: getStickyLeft(4),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 5
            ? {
              "& td:nth-of-type(5), & th:nth-of-type(5)": {
                position: "sticky",
                left: getStickyLeft(5),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 6
            ? {
              "& td:nth-of-type(6), & th:nth-of-type(6)": {
                // 계좌번호
                position: "sticky",
                left: getStickyLeft(6),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 7
            ? {
              "& td:nth-of-type(7), & th:nth-of-type(7)": {
                position: "sticky",
                left: getStickyLeft(7),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          [`thead th:nth-of-type(-n+${stickyColumnCount})`]: { zIndex: 5 },
          "& .edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "td[contenteditable]": {
            minWidth: "80px",
            cursor: "text",
            ...(isColumnWidthLocked
              ? {
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }
              : {}),
          },
          "& td[contenteditable]::-webkit-scrollbar": { display: "none" },
          "& select": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
            outline: "none",
            cursor: "pointer",
          },
          "& select.edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "& input[type='date']": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
          },
        }}
      >
        <table className="dinersheet-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const headerWidth =
                    header.column.columnDef.size ?? header.column.columnDef.minWidth ?? 80;
                  const lockedHeaderWidth = Math.ceil(measuredColWidths[header.id] ?? headerWidth);
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: isColumnWidthLocked ? lockedHeaderWidth : headerWidth,
                        ...(isColumnWidthLocked
                          ? { minWidth: lockedHeaderWidth, maxWidth: lockedHeaderWidth }
                          : {}),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const colKey = cell.column.columnDef.accessorKey;
                  const currentValue = row.getValue(colKey);
                  const cellWidth = Math.ceil(
                    measuredColWidths[cell.column.id] ??
                    cell.column.columnDef.size ??
                    cell.column.columnDef.minWidth ??
                    80
                  );
                  const originalValue = originals?.[rowIndex]?.[colKey];
                  const isNewRow = !String(row.original?.member_id ?? "").trim();

                  const isNumeric = numericCols.includes(colKey);
                  const isImage = imageFields.includes(colKey);

                  // ✅ 이미지 변경 판정 포함
                  const normCurrent = isImage
                    ? typeof currentValue === "object" && currentValue
                      ? "__FILE__"
                      : String(currentValue ?? "")
                    : isNumeric
                      ? Number(currentValue ?? 0)
                      : String(currentValue ?? "");

                  const normOriginal = isImage
                    ? String(originalValue ?? "")
                    : isNumeric
                      ? Number(originalValue ?? 0)
                      : String(originalValue ?? "");

                  const isChanged = normCurrent !== normOriginal;

                  const isEditable = !nonEditableCols.has(colKey);
                  const isSelect = selectFields.has(colKey);
                  const isDate = dateFields.has(colKey);
                  const isInsuranceDate = insuranceDateFields.has(colKey);
                  const canEditMaskedSensitiveField = canEditSensitiveField(
                    colKey,
                    currentValue,
                    maskingEnabled,
                    {},
                    { isNewRow }
                  );
                  const isRetiredRow =
                    String(row.getValue("del_yn") ?? "")
                      .trim()
                      .toUpperCase() === "Y";
                  const needsDelDateCheck =
                    colKey === "del_dt" && isRetiredRow && !formatDateForInput(currentValue);

                  const handleCellChange = (newValue) => {
                    lockColumnsForEditing();
                    const updatedRows = rows.map((r, idx) => {
                      if (idx !== rowIndex) return r;

                      // ✅ position_type 변경 시:
                      // - 유틸(6)  -> account_id=2
                      // - 통합(7)  -> account_id=1
                      // - 일반직 전환 시 기존 1/2 값이면 선택 거래처로 복구
                      if (colKey === "position_type") {
                        const nextPos = String(newValue);

                        const nextAccount =
                          nextPos === UTIL_POSITION
                            ? UTIL_ACCOUNT_ID
                            : nextPos === INTEGRATION_POSITION
                              ? INTEGRATION_ACCOUNT_ID
                              : [UTIL_ACCOUNT_ID, INTEGRATION_ACCOUNT_ID].includes(
                                String(r.account_id ?? "")
                              )
                                ? (selectedAccountId || (accountList?.[0]?.account_id ?? ""))
                                : String(r.account_id ?? "");

                        return {
                          ...r,
                          position_type: newValue,
                          account_id: nextAccount,
                          total: calculateTotal({ ...r, position_type: newValue }),
                        };
                      }

                      // ✅ work_system 변경 시 start/end 자동 세팅
                      if (colKey === "idx") {
                        const selected = (workSystemList || []).find(
                          (w) => String(w.idx) === String(newValue)
                        );

                        return {
                          ...r,
                          idx: newValue,
                          start_time: selected?.start_time
                            ? normalizeTime(selected.start_time)
                            : r.start_time,
                          end_time: selected?.end_time
                            ? normalizeTime(selected.end_time)
                            : r.end_time,
                          total: calculateTotal({
                            ...r,
                            idx: newValue,
                            start_time: selected?.start_time
                              ? normalizeTime(selected.start_time)
                              : r.start_time,
                            end_time: selected?.end_time
                              ? normalizeTime(selected.end_time)
                              : r.end_time,
                          }),
                        };
                      }

                      return {
                        ...r,
                        [colKey]: newValue,
                        total: calculateTotal({ ...r, [colKey]: newValue }),
                      };
                    });

                    setActiveRows(applyUtilAccountId(updatedRows));
                  };

                  // ✅ 이미지 컬럼 렌더링 (RecSheet 방식 그대로)
                  if (isImage) {
                    const value = currentValue ?? "";
                    const hasImage = !!value;
                    const inputId = `upload-${colKey}-${rowIndex}`;

                    return (
                      <td
                        key={cell.id}
                        className={isChanged ? "edited-cell" : ""}
                        style={{
                          textAlign: "center",
                          ...(isColumnWidthLocked
                            ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                            : {}),
                        }}
                      >
                        <input
                          type="file"
                          accept={HEADOFFICE_DOCUMENT_FILE_ACCEPT}
                          id={inputId}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              e.target.value = null;
                              return;
                            }
                            if (!isHeadOfficeDocumentSupportedFile(file)) {
                              Swal.fire(
                                "지원하지 않는 파일 형식",
                                "이미지, PDF, Excel(xls/xlsx) 파일만 첨부할 수 있습니다.",
                                "warning"
                              );
                              e.target.value = null;
                              return;
                            }
                            handleCellChange(file);
                            e.target.value = null;
                          }}
                        />

                        {hasImage ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeof value === "string" && (
                              <Tooltip title="다운로드">
                                <IconButton
                                  size="small"
                                  sx={{ ...fileIconSx, p: 0.5 }}
                                  onClick={() => handleDownload(value)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title="미리보기">
                              <IconButton
                                size="small"
                                sx={{ ...fileIconSx, color: isChanged ? "#d32f2f" : fileIconSx.color, p: 0.5 }}
                                onClick={() => handleViewImage(row.original, colKey)}
                              >
                                <ImageSearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <label htmlFor={inputId}>
                              <MDButton
                                size="small"
                                component="span"
                                color="info"
                                sx={{
                                  fontSize: isMobile ? "10px" : "11px",
                                  minWidth: 44,
                                  px: 1,
                                  py: 0.5,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                변경
                              </MDButton>
                            </label>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <label htmlFor={inputId} style={{ display: "inline-flex" }}>
                              <MDButton
                                size="small"
                                component="span"
                                color="info"
                                sx={{
                                  fontSize: isMobile ? "10px" : "11px",
                                  minWidth: 44,
                                  px: 1,
                                  py: 0.5,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                업로드
                              </MDButton>
                            </label>
                          </div>
                        )}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={cell.id}
                      style={{
                        ...(isColumnWidthLocked
                          ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                          : {}),
                        textAlign: [
                          "rrn",
                          "account_number",
                          "phone",
                          "contract_type",
                          "join_dt",
                          "act_join_dt",
                          "ret_set_dt",
                          "loss_major_insurances",
                          "del_yn",
                          "display_yn",
                          "del_dt",
                          "idx",
                          "start_time",
                          "end_time",
                          "national_pension",
                          "health_insurance",
                          "industrial_insurance",
                          "employment_insurance",
                        ].includes(colKey)
                          ? "center"
                          : colKey === "salary"
                            ? "right"
                            : "left",
                      }}
                      contentEditable={
                        isEditable &&
                        !isSelect &&
                        !isDate &&
                        !isInsuranceDate &&
                        canEditMaskedSensitiveField
                      }
                      suppressContentEditableWarning
                      className={isEditable && isChanged ? "edited-cell" : ""}
                      onFocus={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            lockColumnsForEditing();
                            keepEditableTailVisible(e.currentTarget);
                          }
                          : undefined
                      }
                      onInput={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            lockColumnsForEditing();
                            keepEditableTailVisible(e.currentTarget);
                            let draftValue = e.currentTarget.innerText.trim();
                            if (isNumeric) draftValue = parseNumber(draftValue);
                            const normalizedDraft = isNumeric
                              ? Number(draftValue ?? 0)
                              : String(draftValue ?? "");
                            e.currentTarget.classList.toggle(
                              "edited-cell",
                              normalizedDraft !== normOriginal
                            );
                          }
                          : undefined
                      }
                      onBlur={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            let newValue = e.target.innerText.trim();
                            if (isNumeric) newValue = parseNumber(newValue);
                            handleCellChange(newValue);

                            if (isNumeric) {
                              e.currentTarget.innerText = formatNumber(newValue);
                            }
                          }
                          : undefined
                      }
                    >
                      {isInsuranceDate ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                          }}
                        >
                          <select
                            value={getInsuranceStatus(currentValue)}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (next === "N") {
                                handleCellChange("미해당");
                                return;
                              }
                              if (next === "Y") {
                                const existingDate = formatDateForInput(currentValue);
                                handleCellChange(existingDate || dayjs().format("YYYY-MM-DD"));
                                return;
                              }
                              handleCellChange("");
                            }}
                            className={isChanged ? "edited-cell" : ""}
                            style={{
                              minWidth: 68,
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <option value="">선택</option>
                            <option value="Y">해당</option>
                            <option value="N">미해당</option>
                          </select>

                          {getInsuranceStatus(currentValue) === "Y" && (
                            <input
                              type="date"
                              value={formatDateForInput(currentValue)}
                              onChange={(e) => handleCellChange(e.target.value)}
                              className={isChanged ? "edited-cell" : ""}
                            />
                          )}
                        </div>
                      ) : isSelect ? (
                        colKey === "idx" ? (
                          <Autocomplete
                            size="small"
                            options={(workSystemList || []).map((w) => ({
                              value: String(w.idx),
                              label: w.work_system,
                            }))}
                            value={(() => {
                              const v = String(currentValue ?? "");
                              return (
                                (workSystemList || [])
                                  .map((w) => ({ value: String(w.idx), label: w.work_system }))
                                  .find((o) => o.value === v) || null
                              );
                            })()}
                            onChange={(_, opt) => handleCellChange(opt ? opt.value : "")}
                            getOptionLabel={(opt) => opt?.label ?? ""}
                            isOptionEqualToValue={(opt, val) => opt.value === val.value}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                style={{
                                  fontSize: "12px",
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                  color: isChanged ? "#d32f2f" : "inherit",
                                  fontWeight: isChanged ? 600 : 400,
                                }}
                              >
                                {option.label}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="검색"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    fontSize: "12px",
                                    padding: 0,
                                    color: isChanged ? "#d32f2f" : "inherit",
                                    fontWeight: isChanged ? 600 : 400,
                                  },
                                }}
                              />
                            )}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": { minHeight: 24 },
                              "& .MuiAutocomplete-input": {
                                fontSize: "12px",
                                padding: "0px !important",
                                color: isChanged ? "#d32f2f" : "inherit",
                                fontWeight: isChanged ? 600 : 400,
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 18,
                                color: isChanged ? "#d32f2f" : "inherit",
                              },
                              "& .MuiAutocomplete-option": { fontSize: "12px", minHeight: 28 },
                            }}
                            ListboxProps={{ style: { fontSize: "12px" } }}
                          />
                        ) : colKey === "account_id" ? (
                          (() => {
                            const isAccountChanged =
                              String(currentValue ?? "") !== String(originalValue ?? "");

                            return (
                              <Autocomplete
                                size="small"
                                options={accountOptions}
                                value={(() => {
                                  const v = String(currentValue ?? "");
                                  return accountOptions.find((o) => o.value === v) || null;
                                })()}
                                onChange={(_, opt) => handleCellChange(opt ? opt.value : "")}
                                getOptionLabel={(opt) => opt?.label ?? ""}
                                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                renderOption={(props, option) => (
                                  <li
                                    {...props}
                                    style={{
                                      fontSize: "12px",
                                      paddingTop: 4,
                                      paddingBottom: 4,
                                      color: isAccountChanged ? "#d32f2f" : "inherit",
                                      fontWeight: isAccountChanged ? 600 : 400,
                                    }}
                                  >
                                    {option.label}
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="검색"
                                    InputProps={{
                                      ...params.InputProps,
                                      disableUnderline: true,
                                    }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: {
                                        fontSize: "12px",
                                        padding: 0,
                                        color: isAccountChanged ? "#d32f2f" : "inherit",
                                        fontWeight: isAccountChanged ? 600 : 400,
                                      },
                                    }}
                                  />
                                )}
                                sx={{
                                  minWidth: 180,
                                  "& .MuiInputBase-root": { minHeight: 24 },
                                  "& .MuiAutocomplete-input": {
                                    fontSize: "12px",
                                    padding: "0px !important",
                                    color: isAccountChanged ? "#d32f2f" : "inherit",
                                    fontWeight: isAccountChanged ? 600 : 400,
                                  },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                    color: isAccountChanged ? "#d32f2f" : "inherit",
                                  },
                                  "& .MuiAutocomplete-option": { fontSize: "12px", minHeight: 28 },
                                }}
                                ListboxProps={{ style: { fontSize: "12px" } }}
                              />
                            );
                          })()
                        ) : (
                          <select
                            value={currentValue ?? ""}
                            onChange={(e) => handleCellChange(e.target.value)}
                            className={isChanged ? "edited-cell" : ""}
                            style={{
                              width: "100%",
                              background: "transparent",
                              cursor: "pointer",
                              border: "none",
                            }}
                          >
                            {colKey === "cor_type" &&
                              corOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "del_yn" &&
                              delOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "display_yn" &&
                              displayOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "position_type" &&
                              positionOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "contract_type" &&
                              contractOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "start_time" && (
                              <>
                                <option value="">없음</option>
                                {startTimes.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </>
                            )}

                            {colKey === "end_time" && (
                              <>
                                <option value="">없음</option>
                                {endTimes.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                        )
                      ) : isDate ? (
                        colKey === "del_dt" ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                            }}
                          >
                            <input
                              type="date"
                              value={formatDateForInput(currentValue)}
                              onChange={(e) => handleCellChange(e.target.value)}
                              className={isChanged ? "edited-cell" : ""}
                            />
                            {needsDelDateCheck && (
                              <span style={{ color: "#d32f2f", fontWeight: 600, fontSize: 11 }}>
                                퇴사일 확인
                              </span>
                            )}
                          </div>
                        ) : (
                          <input
                            type="date"
                            value={formatDateForInput(currentValue)}
                            onChange={(e) => handleCellChange(e.target.value)}
                            className={isChanged ? "edited-cell" : ""}
                          />
                        )
                      ) : (
                        (isNumeric
                          ? formatNumber(currentValue)
                          : maskSensitiveFieldValue(colKey, currentValue, maskingEnabled)) ?? ""
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>
    );
  };

  // 통합/유틸 관리 모달에서 업장명 표시용 맵
  const utilAccountNameMap = useMemo(() => {
    const m = new Map();
    (utilAccountRows || []).forEach((a) => {
      m.set(String(a.account_id), a.account_name);
    });
    return m;
  }, [utilAccountRows]);

  if (loading) return <LoadingScreen />;

  return (
    <>
      <AccountMemberToolbar
        isMobile={isMobile}
        isCompactLayout={isTabletOrSmallDesktop}
        activeStatus={activeStatus}
        onActiveStatusChange={handleActiveStatusChange}
        accountOptions={accountOptions}
        selectedAccountId={selectedAccountId}
        onSelectAccount={handleToolbarAccountSelect}
        onSearchMember={handleToolbarMemberSearch}
        onOpenWorkSystemModal={openWorkSystemModal}
        onOpenUtilModal={openUtilModal}
        onOpenDispatchModal={openDispatchModal}
        onDownloadExcel={handleExcelDownloadAllAccounts}
        excelDownloading={excelDownloading}
        maskingEnabled={maskingEnabled}
        onToggleMasking={handleToggleMasking}
        onAddRow={handleAddRow}
        onSave={handleSave}
      />

      <MDBox pt={1} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {renderTable(table, activeRows, originalRows)}
          </Grid>
        </Grid>
      </MDBox>

      {/* =========================
          ✅ 근무형태 모달(기존 그대로)
         ========================= */}
      <Modal open={wsOpen} onClose={closeWorkSystemModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95vw" : 720,
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <MDTypography variant="h6">근무형태 관리</MDTypography>

            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="success" onClick={handleWsAddRow}>
                행추가
              </MDButton>
              <MDButton variant="gradient" color="info" onClick={handleWsSave}>
                저장
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={closeWorkSystemModal}>
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              bgcolor: "#fff",
            }}
          >
            <MDBox
              sx={{
                p: 2,
                "& table": {
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                },
                "& th, & td": {
                  border: "1px solid #686D76",
                  padding: "6px",
                  fontSize: "12px",
                  textAlign: "center",
                  backgroundColor: "#fff",
                },
                "& thead th": {
                  position: "sticky",
                  top: 0,
                  zIndex: 20,
                  backgroundColor: "#f0f0f0",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.12)",
                  backgroundClip: "padding-box",
                },
                "& input, & select": {
                  width: "100%",
                  fontSize: "12px",
                  padding: "6px",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                },
                "& .edited-cell": { color: "#d32f2f", fontWeight: 600 },
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>순번</th>
                    <th>근무형태명</th>
                    <th style={{ width: 140 }}>시작</th>
                    <th style={{ width: 140 }}>마감</th>
                  </tr>
                </thead>

                <tbody>
                  {(wsRows || []).map((r, i) => {
                    const o = wsOriginal?.[i] || {};
                    const isNewRow = r.idx == null || !wsOriginal?.[i];

                    const changedWorkSystem =
                      String(r.work_system ?? "") !== String(o.work_system ?? "");
                    const changedStartTime =
                      String(r.start_time ?? "") !== String(o.start_time ?? "");
                    const changedEndTime = String(r.end_time ?? "") !== String(o.end_time ?? "");

                    return (
                      <tr
                        key={`${r.idx ?? "new"}-${i}`}
                        className={isNewRow ? "edited-cell" : ""}
                        onContextMenu={(e) => handleWsContextMenu(e, i, r.idx)}
                        style={{ cursor: "context-menu" }}
                      >
                        <td className={isNewRow ? "edited-cell" : ""}>{i + 1}</td>

                        <td className={isNewRow || changedWorkSystem ? "edited-cell" : ""}>
                          <input
                            value={r.work_system ?? ""}
                            onChange={(e) => handleWsChange(i, "work_system", e.target.value)}
                            placeholder="예) 주5일(09~18)"
                            className={isNewRow || changedWorkSystem ? "edited-cell" : ""}
                          />
                        </td>

                        <td className={isNewRow || changedStartTime ? "edited-cell" : ""}>
                          <select
                            value={r.start_time ?? ""}
                            onChange={(e) => handleWsChange(i, "start_time", e.target.value)}
                            className={isNewRow || changedStartTime ? "edited-cell" : ""}
                          >
                            <option value="">없음</option>
                            {startTimes.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className={isNewRow || changedEndTime ? "edited-cell" : ""}>
                          <select
                            value={r.end_time ?? ""}
                            onChange={(e) => handleWsChange(i, "end_time", e.target.value)}
                            className={isNewRow || changedEndTime ? "edited-cell" : ""}
                          >
                            <option value="">없음</option>
                            {endTimes.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>

      {wsCtxMenu.open && (
        <div
          onClick={closeWsCtxMenu}
          onContextMenu={(e) => { e.preventDefault(); closeWsCtxMenu(); }}
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 10000 }}
        >
          <div
            style={{
              position: "absolute",
              top: wsCtxMenu.mouseY,
              left: wsCtxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={handleWsDeleteRow}
            >
              🗑️ 행 삭제
            </button>
          </div>
        </div>
      )}

      {/* =========================
          ✅ 통합/유틸 관리 모달 (추가)
         ========================= */}
      <Modal open={utilOpen} onClose={closeUtilModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "98vw" : "92vw",
            maxWidth: 1200,
            height: isMobile ? "90vh" : "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <MDTypography variant="h6">통합/유틸 관리</MDTypography>

            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" onClick={handleUtilSave}>
                저장
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={closeUtilModal}>
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              display: "flex",
              gap: 1,
              p: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
              "& .edited-cell": { color: "#d32f2f", fontWeight: 600 },
            }}
          >
            {/* 왼쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 240,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  통합/유틸 직원 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 클릭 → 가운데 매핑 조회
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilMemberRows || []).map((r, i) => {
                      const selected =
                        String(utilSelectedMember?.member_id ?? "") === String(r.member_id ?? "");
                      const posLabel = getPositionLabel(r.position_type, r.position);

                      return (
                        <tr
                          key={`${r.member_id ?? "m"}-${i}`}
                          onClick={() => handleSelectUtilMember(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{r.name ?? ""}</td>
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데 테이블 */}
            <MDBox
              sx={{
                flex: 1.2,
                minWidth: 360,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  매핑 목록 (가운데)
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  선택된 통합/유틸:{" "}
                  <b>{utilSelectedMember?.member_id ? utilSelectedMember.member_id : "-"}</b>
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>순번</th>
                      <th style={{ width: 120 }}>고객사</th>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilMappingRows || []).map((r, i) => {
                      const selected = utilSelectedMappingRowIndex === i;
                      const isNewUtilRow = r?.idx == null;
                      const posLabel = getPositionLabel(r.position_type, r.position);
                      const accName = utilAccountNameMap.get(String(r.account_id ?? "")) ?? "";
                      const accText = accName;

                      return (
                        <tr
                          key={`${r.idx ?? "new"}-${r.account_id ?? "a"}-${i}`}
                          onClick={() => setUtilSelectedMappingRowIndex(i)}
                          onContextMenu={(e) => handleUtilMappingContextMenu(e, i)}
                          className={isNewUtilRow ? "edited-cell" : ""}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(255,193,7,0.12)" : "#fff",
                          }}
                        >
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{r.idx ?? ""}</td>
                          <td
                            className={isNewUtilRow ? "edited-cell" : ""}
                            style={{ textAlign: "left" }}
                          >
                            {accText}
                          </td>
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{r.name ?? ""}</td>
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데-오른쪽 컨트롤 */}
            <MDBox
              sx={{
                width: isMobile ? 54 : 70,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleAddMappingFromRight}
                sx={{ minWidth: isMobile ? 46 : 56, px: 0 }}
              >
                {"<"}
              </MDButton>
              <MDTypography variant="caption" sx={{ color: "#666", textAlign: "center" }}>
                업장 → 매핑
              </MDTypography>
            </MDBox>

            {/* 오른쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 300,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  업장 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 선택 후 &lt; 버튼
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>고객사</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilAccountRows || []).map((r, i) => {
                      const selected =
                        String(utilSelectedAccount?.account_id ?? "") ===
                        String(r.account_id ?? "");
                      return (
                        <tr
                          key={`${r.account_id ?? "a"}-${i}`}
                          onClick={() => setUtilSelectedAccount(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ textAlign: "left", fontWeight: selected ? 700 : 400 }}>
                            {r.account_name ?? ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>

      {/* ✅ 통합/유틸 매핑 목록(가운데) 우클릭 메뉴 - 직책이 통합일 때만 노출 */}
      {utilMappingCtxMenu.open && (
        <div
          onClick={closeUtilMappingCtxMenu}
          onContextMenu={(e) => { e.preventDefault(); closeUtilMappingCtxMenu(); }}
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 10000 }}
        >
          <div
            style={{
              position: "absolute",
              top: utilMappingCtxMenu.mouseY,
              left: utilMappingCtxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={handleUtilMappingDelete}
            >
              🗑️ 삭제 처리
            </button>
          </div>
        </div>
      )}

      {/* =========================
      ✅ 직원파출관리 모달 (통합/유틸 관리 복제 + 연/월 선택)
      ========================= */}
      <Modal open={dispatchOpen} onClose={closeDispatchModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "98vw" : "97vw",
            maxWidth: 1500,
            height: isMobile ? "90vh" : "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              gap: 1,
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1} sx={{ flexWrap: "wrap" }}>
              <MDTypography variant="h6">직원파출관리</MDTypography>
            </MDBox>

            {/* ✅ 거래처 선택(왼쪽 직원목록 필터) */}
            <DispatchAccountAutocomplete
              accountOptions={accountOptions}
              selectedOption={selectedDispatchAccountOption}
              onChange={handleDispatchAccountSelect}
            />

            <MDBox display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleDispatchSave}
                disabled={dispatchSaving}
              >
                {dispatchSaving ? "저장중..." : "저장"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={closeDispatchModal}
                disabled={dispatchSaving}
              >
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              display: "flex",
              gap: 1,
              p: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
              "& .edited-cell": { color: "#d32f2f", fontWeight: 600 },
            }}
          >
            {/* 왼쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 240,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  직원 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 클릭 → 가운데 매핑 조회
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": { position: "sticky", top: 0, bgcolor: "#f7f7f7", zIndex: 2 },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dispatchMemberRows || []).map((r, i) => {
                      const selected =
                        String(dispatchSelectedMember?.member_id ?? "") ===
                        String(r.member_id ?? "");
                      const posLabel = getPositionLabel(r.position_type, r.position);

                      return (
                        <tr
                          key={`${r.member_id ?? "m"}-${i}`}
                          onClick={() => handleSelectDispatchMember(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{r.name ?? ""}</td>
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데 테이블 */}
            <MDBox
              sx={{
                flex: 2.1, // ✅ 가운데 영역 가로폭 확대
                minWidth: 760, // ✅ 가운데 영역 최소폭 확대
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  매핑 목록 (가운데)
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  선택된 직원:{" "}
                  <b>
                    {dispatchSelectedMember?.member_id ? dispatchSelectedMember.member_id : "-"}
                  </b>
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": { position: "sticky", top: 0, bgcolor: "#f7f7f7", zIndex: 2 },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>순번</th>
                      <th style={{ width: 220 }}>소속 고객사</th>
                      <th style={{ width: 220 }}>파견 고객사</th>
                      <th style={{ width: 160 }}>파견일자</th>
                      <th style={{ width: 120 }}>시작</th>
                      <th style={{ width: 120 }}>마감</th>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dispatchMappingRows || []).map((r, i) => {
                      const selected = dispatchSelectedMappingRowIndex === i;
                      const originalDispatchRow =
                        r?.idx != null ? dispatchOriginalMappingRowMap.get(String(r.idx)) ?? null : null;
                      const isNewDispatchRow = !originalDispatchRow;
                      const changedDispatchDate =
                        !isNewDispatchRow &&
                        normalizeDispatchDate(r.record_date ?? "") !==
                        normalizeDispatchDate(originalDispatchRow?.record_date ?? "");
                      const changedDispatchStartTime =
                        !isNewDispatchRow &&
                        normalizeTime(r.start_time ?? "") !==
                        normalizeTime(originalDispatchRow?.start_time ?? "");
                      const changedDispatchEndTime =
                        !isNewDispatchRow &&
                        normalizeTime(r.end_time ?? "") !==
                        normalizeTime(originalDispatchRow?.end_time ?? "");

                      const posLabel = getPositionLabel(r.position_type, r.position);

                      const ownerAccId = String(r.account_id ?? "");
                      const ownerAccName = memberAccountNameMap.get(ownerAccId) ?? "";
                      const ownerText = ownerAccName;

                      const dispAccId = String(r.dispatch_account_id ?? "");
                      const dispAccName = dispatchAccountNameMap.get(dispAccId) ?? "";
                      const dispText = dispAccName;
                      const normalizedStartTime = normalizeTime(r.start_time ?? "");
                      const normalizedEndTime = normalizeTime(r.end_time ?? "");
                      const startTimeOptions = startTimes.includes(normalizedStartTime)
                        ? startTimes
                        : normalizedStartTime
                          ? [normalizedStartTime, ...startTimes]
                          : startTimes;
                      const endTimeOptions = endTimes.includes(normalizedEndTime)
                        ? endTimes
                        : normalizedEndTime
                          ? [normalizedEndTime, ...endTimes]
                          : endTimes;

                      return (
                        <tr
                          key={`${r.idx ?? "new"}-${ownerAccId}-${dispAccId}-${i}`}
                          onClick={() => setDispatchSelectedMappingRowIndex(i)}
                          onContextMenu={(e) => handleDispatchMappingContextMenu(e, i)}
                          className={isNewDispatchRow ? "edited-cell" : ""}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(255,193,7,0.12)" : "#fff",
                          }}
                        >
                          <td className={isNewDispatchRow ? "edited-cell" : ""}>{r.idx ?? ""}</td>
                          <td
                            className={isNewDispatchRow ? "edited-cell" : ""}
                            style={{ textAlign: "left" }}
                          >
                            {ownerText}
                          </td>
                          <td
                            className={isNewDispatchRow ? "edited-cell" : ""}
                            style={{ textAlign: "left" }}
                          >
                            {dispText}
                          </td>

                          {/* ✅ 파견일자: 달력 선택 */}
                          <td className={isNewDispatchRow || changedDispatchDate ? "edited-cell" : ""}>
                            <input
                              type="date"
                              value={r.record_date ?? ""}
                              onChange={(e) =>
                                handleDispatchRowChange(i, "record_date", e.target.value)
                              }
                              className={isNewDispatchRow || changedDispatchDate ? "edited-cell" : ""}
                              style={{
                                width: "100%",
                                fontSize: 12,
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                cursor: "pointer",
                              }}
                            />
                          </td>

                          {/* ✅ 시작/마감 시간도 가운데 테이블에서 바로 수정 가능 */}
                          <td
                            className={isNewDispatchRow || changedDispatchStartTime ? "edited-cell" : ""}
                          >
                            <select
                              value={normalizedStartTime}
                              onChange={(e) =>
                                handleDispatchRowChange(i, "start_time", normalizeTime(e.target.value))
                              }
                              className={
                                isNewDispatchRow || changedDispatchStartTime ? "edited-cell" : ""
                              }
                              style={{
                                width: "100%",
                                fontSize: 12,
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">-</option>
                              {startTimeOptions.map((t) => (
                                <option key={`dispatch-start-${t}`} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className={isNewDispatchRow || changedDispatchEndTime ? "edited-cell" : ""}>
                            <select
                              value={normalizedEndTime}
                              onChange={(e) =>
                                handleDispatchRowChange(i, "end_time", normalizeTime(e.target.value))
                              }
                              className={isNewDispatchRow || changedDispatchEndTime ? "edited-cell" : ""}
                              style={{
                                width: "100%",
                                fontSize: 12,
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">-</option>
                              {endTimeOptions.map((t) => (
                                <option key={`dispatch-end-${t}`} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className={isNewDispatchRow ? "edited-cell" : ""}>{r.name ?? ""}</td>
                          <td className={isNewDispatchRow ? "edited-cell" : ""}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데-오른쪽 컨트롤 */}
            <MDBox
              sx={{
                width: isMobile ? 54 : 70,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleAddDispatchMappingFromRight}
                sx={{ minWidth: isMobile ? 46 : 56, px: 0 }}
              >
                {"<"}
              </MDButton>
              <MDTypography variant="caption" sx={{ color: "#666", textAlign: "center" }}>
                업장 → 매핑
              </MDTypography>
            </MDBox>

            {/* 오른쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 300,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  업장 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 선택 후 &lt; 버튼
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": { position: "sticky", top: 0, bgcolor: "#f7f7f7", zIndex: 2 },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>고객사</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dispatchAccountRows || []).map((r, i) => {
                      const selected =
                        String(dispatchSelectedAccount?.account_id ?? "") ===
                        String(r.account_id ?? "");
                      return (
                        <tr
                          key={`${r.account_id ?? "a"}-${i}`}
                          onClick={() => setDispatchSelectedAccount(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ textAlign: "left", fontWeight: selected ? 700 : 400 }}>
                            {r.account_name ?? ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>

      {dispatchCtxMenu.open && (
        <div
          onClick={closeDispatchCtxMenu}
          onContextMenu={(e) => {
            e.preventDefault();
            closeDispatchCtxMenu();
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: dispatchCtxMenu.mouseY,
              left: dispatchCtxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={() => handleDeleteDispatchMappingRow(dispatchCtxMenu.rowIndex)}
            >
              🗑️ 행 삭제
            </button>
          </div>
        </div>
      )}

      {/* 첨부파일 공통 미리보기 오버레이 */}
      <PreviewOverlay
        open={previewFiles.length > 0}
        files={previewFiles}
        currentIndex={previewIndex}
        onChangeIndex={setPreviewIndex}
        onClose={handleCloseViewer}
      />
    </>
  );
}

export default AccountMemberSheet;
